import { SUPPORTED_LOCALES } from '@platform/i18n';
import type { TFunction } from 'i18next';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addTenantOrigin,
  addToBlacklist,
  type BanCandidate,
  type CreatedTenant,
  type CurrentUser,
  ceaseTenant,
  createTenant,
  type DomainRow,
  type GlobalBan,
  handleAlert,
  impersonate,
  impersonationLog,
  blacklist as loadBlacklist,
  platformAlerts,
  platformDomains,
  platformHealth,
  platformNetworks,
  platformTenant,
  platformTenants,
  reissueInvite,
  removeFromBlacklist,
  removeTenantOrigin,
  setClusterCost,
  setTenantActivation,
  setTenantCluster,
  setTenantDomain,
  setTenantPlan,
  setTenantStatus,
  type TenantReadiness,
  verifyTenantDomain,
} from '../api.ts';
import { FieldHint } from '../components/FieldHint.tsx';
import { InfoTip } from '../components/InfoTip.tsx';
import {
  AccessIcon,
  AlertsIcon,
  BlacklistIcon,
  ClustersIcon,
  DomainsIcon,
  HealthIcon,
  NewTenantIcon,
  TenantsIcon,
} from '../components/PlatformIcons.tsx';
import { formatWhen } from '../format.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { useLoad } from '../hooks/useLoad.ts';
import { errorMessage } from '../messages.ts';

/**
 * Le schermate del fornitore (mock 4).
 *
 * "Interfaccia interna, può essere spartana. Serve a sapere prima dei clienti
 * che qualcosa non va."
 *
 * Sette sezioni: gli enti, la creazione di un ente, i domini e i certificati,
 * gli avvisi, la blacklist globale, il monitoraggio e gli accessi impersonati.
 * Le prime due nuove arrivano con la fase 8, che è l'ultima in assoluto perché
 * "i primi 10-20 tenant si creano a mano" (sezione 10): questa schermata è il
 * momento in cui "a mano" smette di voler dire "con un seed".
 *
 * **Spartana non vuol dire indistinta.** Questa schermata è anche la vetrina
 * che si mostra a un cliente aziendale, e fino a ieri era un elenco di
 * paragrafi grigi: settantotto `p.muted` in cui una nota legale, la conseguenza
 * di un'azione e l'aiuto di un campo avevano lo stesso identico aspetto. Da
 * adesso le quattro cose hanno quattro forme — `.card__sub` spiega la scheda,
 * `.note` porta la conseguenza di ciò che si sta per premere, `<details>`
 * custodisce ciò che si legge una volta sola, e `.muted` resta l'aiuto di un
 * campo — e la parte che si guarda a colpo d'occhio (chi sta male, quanti sono)
 * non è più una frase in mezzo alle altre.
 *
 * Tre cose che questa schermata non fa, e che non sono dimenticanze:
 *
 *  - **non incassa niente.** Lo stato del pagamento è una scelta a mano, presa
 *    quando il bonifico arriva: nel prodotto non esiste nessuna integrazione di
 *    pagamento, per nessun motivo (sezione 9);
 *  - **non interrompe niente.** "Sospendi nuove sessioni" ferma le nuove; le
 *    conversazioni in corso finiscono da sé, e la schermata dice quante sono
 *    proprio perché chi preme il pulsante lo sappia (vincolo 5);
 *  - **non mostra nessun numero di telefono e nessun contenuto.** Il contenuto
 *    non esiste; i numeri non escono da nessuna rotta, e la blacklist porta
 *    impronte accorciate.
 */

/**
 * Le otto sezioni della piattaforma vivono ora nella barra laterale
 * dell'applicazione, non più in una seconda colonna dentro questa pagina
 * (`docs/DECISIONI.md`, BP2): per il fornitore, "dove vai" è la stessa
 * domanda che per lo staff di un ente, e la risposta sta nello stesso posto
 * — non in un pannello a fianco che nel mock non esisteva come cosa a sé.
 * Lo stato di quale sezione è aperta, prima locale a questo componente, ora
 * arriva da fuori (`section`/`onSectionChange`): li' vive anche il pulsante
 * che la cambia (`TopBar.tsx`).
 */
export type PlatformSection =
  | 'tenants'
  | 'new'
  | 'domains'
  | 'alerts'
  | 'blacklist'
  | 'clusters'
  | 'health'
  | 'access';

type Props = {
  user: CurrentUser;
  section: PlatformSection;
  onSectionChange: (section: PlatformSection) => void;
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
};

/**
 * Le otto sezioni, ciascuna con la propria icona.
 *
 * **Una tabella sola.** L'ordine e l'icona stanno insieme perché sono la
 * stessa cosa vista due volte: due elenchi paralleli sono due elenchi che
 * prima o poi divergono, e a divergere sarebbe l'icona sbagliata accanto al
 * nome giusto — cioè l'unico caso in cui un'icona fa danno.
 *
 * L'icona non nomina niente: il nome della sezione è la parola accanto, ed è
 * lei il nome accessibile del pulsante (`PlatformIcons.tsx`).
 *
 * `clusters` era annidata dentro `health`: la lista dei cluster media, il suo
 * costo e "sposta un ente" stavano sotto lo stesso titolo dei KPI in diretta e
 * dei job di retention, tre compiti senza rapporto fra loro accomunati solo
 * dall'essere "roba tecnica". Una voce propria, subito dopo quella da cui è
 * uscita, per chi la cercava già lì (`docs/DECISIONI.md`, BB4).
 *
 * Esportata perché `TopBar.tsx` la usa per disegnare le stesse otto voci
 * nella barra laterale: un elenco solo, non due che potrebbero divergere.
 */
export const PLATFORM_SECTIONS: {
  id: PlatformSection;
  Icon: (props: { className?: string }) => React.ReactElement;
}[] = [
  { id: 'tenants', Icon: TenantsIcon },
  { id: 'new', Icon: NewTenantIcon },
  { id: 'domains', Icon: DomainsIcon },
  { id: 'alerts', Icon: AlertsIcon },
  { id: 'blacklist', Icon: BlacklistIcon },
  { id: 'clusters', Icon: ClustersIcon },
  { id: 'health', Icon: HealthIcon },
  { id: 'access', Icon: AccessIcon },
];

export function PlatformPage({ section, onSectionChange, headingRef }: Props) {
  const { t } = useTranslation();
  const sectionHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousSection = useRef(section);

  /**
   * Il fuoco segue la sezione, da qualunque posto sia arrivato il cambio.
   *
   * Prima il pulsante che cambiava sezione e lo spostamento del fuoco erano
   * la stessa funzione, perché stavano nello stesso componente. Adesso il
   * pulsante vive in `TopBar.tsx` e può cambiare `section` anche da lì (o da
   * "Crea l'ente", che dopo aver creato torna a "tenants" da questa stessa
   * pagina): il fuoco deve seguire il **dato**, non il posto da cui è
   * partito il clic — un frame dopo, perché il titolo della sezione nuova
   * non esiste ancora (WCAG 2.4.3).
   */
  useEffect(() => {
    if (previousSection.current === section) return;
    previousSection.current = section;
    globalThis.requestAnimationFrame?.(() => sectionHeadingRef.current?.focus());
  }, [section]);

  return (
    <main className="page page--panel" id="main" tabIndex={-1}>
      <div className="panel__intro">
        {/*
          Il nome della sezione aperta, non più "Piattaforma" fisso.
          Resta il bersaglio del collegamento di salto e del cambio di
          sezione (WCAG 2.4.3) — quello non cambia, un bersaglio che non si
          vede è un bersaglio che chi naviga da tastiera perde — ma prima era
          la stessa parola su ogni sezione: chi apre "Enti" e poi "Nuovo
          ente" vedeva scritto due volte "Piattaforma", mai il nome di dove
          si trovava. Le otto voci della barra laterale (`PLATFORM_SECTIONS`)
          hanno già una traduzione propria (`platform.section_${id}`, la
          stessa che disegna `TopBar.tsx`): riusarla qui vuol dire lo stesso
          titolo, non uno nuovo da tradurre e tenere allineato.
        */}
        <h2 ref={headingRef} tabIndex={-1}>
          {t(`platform.section_${section}`)}
        </h2>
      </div>

      <div className="panel__body">
        {section === 'tenants' ? <TenantsSection headingRef={sectionHeadingRef} /> : null}
        {section === 'new' ? (
          <NewTenantSection
            headingRef={sectionHeadingRef}
            onCreated={() => onSectionChange('tenants')}
          />
        ) : null}
        {section === 'domains' ? <DomainsSection headingRef={sectionHeadingRef} /> : null}
        {section === 'alerts' ? <AlertsSection headingRef={sectionHeadingRef} /> : null}
        {section === 'blacklist' ? <BlacklistSection headingRef={sectionHeadingRef} /> : null}
        {section === 'clusters' ? <ClustersSection headingRef={sectionHeadingRef} /> : null}
        {section === 'health' ? <HealthSection headingRef={sectionHeadingRef} /> : null}
        {section === 'access' ? <AccessSection headingRef={sectionHeadingRef} /> : null}
      </div>
    </main>
  );
}

/**
 * Le righe che stanno arrivando, disegnate come barre.
 *
 * Prima ogni sezione scriveva "Caricamento…" e, subito sotto, l'elenco vuoto
 * con il proprio "non c'è niente": due affermazioni nella stessa mezza pagina,
 * e la seconda falsa. Qui il vuoto lo dichiara solo chi ha finito di leggere.
 *
 * Le barre sono `aria-hidden`: a chi ascolta la pagina il caricamento lo dice
 * il testo, che resta nel documento e non finisce in una regione live — la
 * regione live di questa schermata serve agli esiti, e riempirla di
 * "Caricamento…" a ogni filtro cambiato la renderebbe rumore.
 */
function Loading({ label }: { label: string }) {
  return (
    <>
      <p className="visually-hidden">{label}</p>
      <div aria-hidden="true">
        <span className="skeleton skeleton--wide" />
        <span className="skeleton skeleton--wide" />
        <span className="skeleton skeleton--half" />
      </div>
    </>
  );
}

/** Non c'è niente, e adesso lo sappiamo: i dati sono arrivati e sono zero. */
function Empty({ text }: { text: string }) {
  return (
    <div className="empty">
      <p>{text}</p>
    </div>
  );
}

// -------------------------------------------------------------------- enti

/**
 * Un ente che sta male, e in quale dei tre modi.
 *
 * È la sola domanda per cui si apre questa schermata — "sapere prima dei
 * clienti che qualcosa non va" — e fino a ieri la risposta era in due
 * paragrafi grigi sotto una riga grigia, cioè da nessuna parte. Le tre
 * condizioni sono quelle che portano a una telefonata: il canone non è
 * arrivato, i minuti inclusi sono finiti, le nuove conversazioni sono ferme.
 *
 * Restano parole, non colori: la riga evidenziata porta le stesse pastiglie
 * scritte che si leggono senza distinguere una tinta dall'altra (WCAG 1.4.1).
 */
type TenantRow = {
  paymentStatus: string;
  minutes: number;
  includedMinutes: number;
  newSessionsSuspended: boolean;
};

function tenantFlags(tenant: TenantRow): string[] {
  const flags: string[] = [];
  if (tenant.paymentStatus === 'overdue') flags.push('platform.flag_overdue');
  if (tenant.minutes > tenant.includedMinutes) flags.push('platform.flag_over_plan');
  if (tenant.newSessionsSuspended) flags.push('platform.state_suspended');
  return flags;
}

function TenantsSection({
  headingRef,
}: {
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string>();
  const { say, announcer } = useAnnounce();

  const read = useCallback(async (signal: AbortSignal) => {
    /**
     * Le reti sono un di più: se falliscono, gli enti si vedono lo stesso.
     * Con `all` una rete che non risponde lasciava vuota anche la schermata
     * principale del fornitore.
     */
    const [list, nets] = await Promise.allSettled([
      platformTenants({ signal }),
      platformNetworks({ signal }),
    ]);
    return {
      tenants: list.status === 'fulfilled' ? list.value.tenants : undefined,
      networks: nets.status === 'fulfilled' ? nets.value.networks : undefined,
      /** Il primo dei due che è andato storto, se ce n'è uno. */
      failure: [list, nets].find((result) => result.status === 'rejected')?.reason,
    };
  }, []);

  const {
    data,
    failed,
    reload: load,
  } = useLoad(read, {
    onLoaded: (result) => {
      if (result.failure !== undefined) {
        say(`${t('platform.load_failed')} ${errorMessage(t, result.failure)}`, 'danger');
      }
    },
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });
  const tenants = data?.tenants;
  const networks = data?.networks;

  /** Come nello storico: chiudendo la scheda il fuoco torna al titolo (AJ16). */
  const returning = useRef(false);
  useEffect(() => {
    if (openId || !returning.current) return;
    returning.current = false;
    headingRef.current?.focus();
  }, [openId, headingRef]);

  if (openId) {
    return (
      <TenantCard
        id={openId}
        headingRef={headingRef}
        onBack={() => {
          returning.current = true;
          setOpenId(undefined);
          void load();
        }}
      />
    );
  }

  /*
    Chi sta male viene prima.

    L'ordinamento è stabile — `sort` in JavaScript lo è — quindi fra due enti
    nella stessa condizione resta l'ordine del server: non è una classifica,
    è la metà dell'elenco che va guardata portata in cima, così la si vede
    senza scorrere anche quando gli enti sono cento.
  */
  const ordered = tenants
    ? [...tenants].sort((a, b) => tenantFlags(b).length - tenantFlags(a).length)
    : undefined;
  const troubled = ordered?.filter((tenant) => tenantFlags(tenant).length > 0).length ?? 0;

  return (
    <>
      <section className="card" aria-labelledby="tenants-heading">
        <div className="section-head">
          <h3 id="tenants-heading" ref={headingRef} tabIndex={-1}>
            {t('platform.tenants_title')}
          </h3>
          {/*
            Quanti sono, accanto al titolo: prima il numero esisteva solo per
            chi arrivava in fondo a scorrere.
          */}
          {ordered ? (
            <span className="count">{t('platform.count_tenants', { count: ordered.length })}</span>
          ) : null}
        </div>

        {announcer}

        {/*
          La riga che risponde alla domanda della schermata, prima dell'elenco.
          Non è un avviso di sistema: è il conteggio di ciò che sta sotto, e
          sparisce quando non c'è niente da guardare.
        */}
        {troubled > 0 ? (
          <p className="banner banner--warn">
            {t('platform.tenants_attention', { count: troubled })}
          </p>
        ) : null}

        {!ordered && !failed ? <Loading label={t('admin.loading')} /> : null}
        {ordered && ordered.length === 0 ? <Empty text={t('platform.tenants_empty')} /> : null}

        <ul className="plain-list">
          {ordered?.map((tenant) => {
            const flags = tenantFlags(tenant);
            return (
              <li key={tenant.id} className={flags.length > 0 ? 'row row--flagged' : 'row'}>
                <div className="stack stack--tight">
                  <p className="row__title">{tenant.name}</p>
                  <p className="row__meta">
                    {[
                      t(tenant.integrated ? 'platform.mode_integrated' : 'platform.mode_basic'),
                      t('platform.tenant_usage', {
                        sessions: tenant.sessions,
                        minutes: tenant.minutes,
                        included: tenant.includedMinutes,
                      }),
                      t(`platform.payment_${tenant.paymentStatus}`),
                    ].join(' · ')}
                  </p>
                  {flags.length > 0 ? (
                    <p className="cluster">
                      {flags.map((flag) => (
                        <span key={flag} className="badge badge--closed">
                          {t(flag)}
                        </span>
                      ))}
                    </p>
                  ) : null}
                </div>
                <div className="row__actions">
                  {/*
                    A schermo la parola breve, per esteso nel nome accessibile:
                    in un elenco di cento enti "Apri la scheda di …" scritto
                    cento volte è cento volte la stessa riga da leggere, ma è
                    anche l'unico modo perché chi scorre i pulsanti con uno
                    screen reader sappia quale sta per aprire (WCAG 2.4.6).
                  */}
                  <button
                    type="button"
                    className="button"
                    aria-label={t('platform.open_tenant', { name: tenant.name })}
                    onClick={() => setOpenId(tenant.id)}
                  >
                    {t('platform.open')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="card" aria-labelledby="networks-heading">
        <div className="section-head">
          <div className="section-head__title">
            <h3 id="networks-heading">{t('platform.networks_title')}</h3>
            <InfoTip>{t('platform.networks_hint')}</InfoTip>
          </div>
          {networks ? (
            <span className="count">
              {t('platform.count_networks', { count: networks.length })}
            </span>
          ) : null}
        </div>
        {!networks && !failed ? <Loading label={t('admin.loading')} /> : null}
        {networks && networks.length === 0 ? <Empty text={t('platform.networks_empty')} /> : null}
        <ul className="plain-list">
          {networks?.map((network) => (
            <li key={network.id} className="report">
              <p className="row__title">{network.name}</p>
              <p className="row__meta">
                {t('platform.network_pool', {
                  used: network.usedMinutes,
                  included: network.includedMinutes,
                  tenants: network.tenants.length,
                  max: network.maxTenants ?? '—',
                })}
              </p>
              <ul className="plain-list">
                {network.tenants.map((member) => (
                  <li key={member.id} className="row">
                    <div>
                      <p>{member.name}</p>
                      <p className="row__meta">
                        {t('platform.network_member', {
                          minutes: member.minutes,
                          sessions: member.sessions,
                        })}
                      </p>
                    </div>
                    {member.capMinutes && member.minutes > member.capMinutes ? (
                      <span className="badge badge--closed">
                        {t('platform.over_cap', { cap: member.capMinutes })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              {network.contact ? (
                <p className="row__meta">
                  {t('platform.network_contact', { name: network.contact.displayName })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
        {/*
          Il vincolo 7 vale per tutte le reti, e va detto una volta: ripetuto
          dentro ogni riquadro di rete era la stessa frase legale letta otto
          volte da chi ascolta la pagina, e sbiadita per chi la guarda.
        */}
        <p className="note">{t('platform.network_privacy_note')}</p>
      </section>
    </>
  );
}

/**
 * Le quattro decisioni che il fornitore può prendere su un ente, e che
 * passano tutte da una conferma.
 *
 * Prima erano quattro gruppi di pulsanti a livello piatto — pagamento,
 * sospensione, attivazione, dominio — e un campo "motivo" unico in cima alla
 * scheda, a quindici centimetri dall'ultimo pulsante che ne dipendeva: chi
 * premeva "Sospendi" non aveva più sotto gli occhi il campo da cui quel
 * pulsante dipende, e chi non vede scopriva che mancava qualcosa solo perché
 * l'azione non partiva.
 *
 * Adesso ogni azione apre la stessa scheda di conferma della moderazione e
 * della blacklist: il perché sta accanto alla decisione, la decisione è
 * dichiarata per esteso nel titolo, e nessuna delle quattro parte al primo
 * tocco. È anche il motivo per cui non serve più distinguere il peso dei
 * pulsanti con il colore: il peso è nel secondo passo.
 */
type Asking =
  | { kind: 'payment'; status: 'current' | 'due_soon' | 'overdue' }
  | { kind: 'suspension'; suspend: boolean }
  | { kind: 'activation'; activate: boolean }
  | { kind: 'domain'; domain: string | null }
  | { kind: 'origin_add'; origin: string }
  | { kind: 'origin_remove'; originId: string; origin: string }
  | { kind: 'plan'; plan: string; includedMinutes: number }
  | { kind: 'invite'; role: 'volunteer' | 'supervisor' | 'coordinator'; email: string };

/**
 * La scheda di un ente (mock 4, `s-tenant`).
 *
 * Da qui si fanno le due sole cose che il fornitore può fare a un ente, ed
 * entrambe chiedono un perché che finisce nel registro **dell'ente**: cambiare
 * lo stato amministrativo, ed entrarci per assistenza.
 *
 * **Tre schede, non una.** Prima erano tredici coppie di dati, un campo
 * obbligatorio e cinque compiti di rischio diversissimo dentro un unico
 * riquadro, separati da titoli di terzo livello: cambiare uno stato di
 * pagamento aveva lo stesso peso visivo di entrare nei dati di un ente. Le tre
 * schede dicono cosa si sta facendo prima ancora che si legga: si guarda
 * l'ente, si interviene sull'ente, si entra nell'ente — e l'ultima è l'unica
 * cosa in fondo alla pagina, dove non ci si arriva per sbaglio.
 */
function TenantCard({
  id,
  headingRef,
  onBack,
}: {
  id: string;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  /** Il perché dell'intervento in corso: vive quanto la scheda di conferma. */
  const [reason, setReason] = useState('');
  /** Il perché dell'accesso impersonato, che è un modulo suo e resta suo. */
  const [enterReason, setEnterReason] = useState('');
  const [code, setCode] = useState('');
  /** La cessazione: modulo suo, come l'accesso impersonato, con il suo codice. */
  const [ceaseReason, setCeaseReason] = useState('');
  const [ceaseCode, setCeaseCode] = useState('');
  const [domain, setDomain] = useState('');
  const [originValue, setOriginValue] = useState('');
  const [planValue, setPlanValue] = useState('');
  const [minutesValue, setMinutesValue] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'volunteer' | 'supervisor' | 'coordinator'>(
    'coordinator',
  );
  /**
   * Il nuovo collegamento d'invito, mostrato **una volta sola** come alla
   * creazione (AK3): resta finché non lo si chiude a mano, non sparisce da
   * solo con un annuncio temporaneo, perché serve tempo per copiarlo.
   */
  const [reissuedInvite, setReissuedInvite] = useState<{
    url: string;
    expiresAt: string;
    email: string;
    role: 'volunteer' | 'supervisor' | 'coordinator';
  } | null>(null);
  const reissuedRef = useRef<HTMLHeadingElement>(null);
  const [asking, setAsking] = useState<Asking>();
  const [reasonMissing, setReasonMissing] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const askingHeadingRef = useRef<HTMLHeadingElement>(null);
  const { say, announcer } = useAnnounce();

  const read = useCallback((signal: AbortSignal) => platformTenant(id, { signal }), [id]);
  /**
   * I campi del dominio e del piano partono da ciò che c'è, e **solo la
   * prima volta**.
   *
   * Riempirli a ogni ricarica voleva dire che cambiare lo stato di pagamento
   * cancellava senza avviso ciò che si stava scrivendo.
   */
  const detailSeeded = useRef(false);
  const {
    data: detail,
    failed,
    reload: load,
  } = useLoad(read, {
    onLoaded: (next) => {
      if (detailSeeded.current) return;
      detailSeeded.current = true;
      setDomain(next.tenant.customDomain ?? '');
      setPlanValue(next.tenant.plan);
      setMinutesValue(String(next.tenant.includedMinutes));
    },
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  useEffect(() => {
    headingRef.current?.focus();
  }, [headingRef]);

  /** Aprendo una conferma il fuoco ci va: è lì che si deve scrivere. */
  useEffect(() => {
    if (asking) askingHeadingRef.current?.focus();
  }, [asking]);

  /**
   * Chiudendo la conferma, il fuoco torna da dove era partito (AJ16).
   *
   * Annullando si torna al pulsante che l'aveva aperta: non è cambiato niente.
   * Confermando si torna al titolo della scheda, perché l'ente viene riletto e
   * quel pulsante può avere cambiato nome o essere sparito — "Sospendi" diventa
   * "Riattiva", e premere due volte lo stesso posto farebbe l'opposto di quello
   * che si voleva.
   *
   * **E l'esito si annuncia dopo aver spostato il fuoco, non prima**: uno
   * spostamento di fuoco interrompe la coda di parlato di NVDA, JAWS e
   * VoiceOver, quindi l'annuncio dato prima veniva zittito dal titolo su cui il
   * fuoco stava arrivando. È la stessa correzione della moderazione e della
   * blacklist, per la stessa ragione.
   */
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const pending = useRef<{
    focus: HTMLButtonElement | null;
    said?: string;
    /**
     * Il reinvio non torna al titolo della scheda: fa comparire un
     * collegamento nuovo, sensibile, mostrato una volta sola (AK3). Senza
     * questo, chi non vede sentirebbe solo "invito rigenerato" col fuoco
     * lontano dal riquadro che contiene il collegamento vero da copiare.
     */
    focusReissued?: boolean;
  } | null>(null);
  // Senza elenco di dipendenze, di proposito: il lavoro va fatto al primo
  // disegno in cui la scheda si è fermata, che non coincide con il cambio di
  // nessuna singola variabile.
  useEffect(() => {
    const job = pending.current;
    if (!job || asking || busy) return;
    pending.current = null;

    const node = job.focus;
    if (node?.isConnected && !node.disabled) node.focus();
    else if (job.focusReissued && reissuedRef.current) reissuedRef.current.focus();
    else headingRef.current?.focus();

    if (job.said) say(job.said);
  });

  /** Chi ha aperto la conferma, per saperci tornare annullando. */
  const ask = (event: React.MouseEvent<HTMLButtonElement>, what: Asking): void => {
    // Un ritorno rimasto in sospeso da un giro precedente non vale più.
    pending.current = null;
    openerRef.current = event.currentTarget;
    /*
      E il modulo riparte **vuoto**. Il motivo scritto per una decisione e non
      confermato restava nel campo aprendone un'altra, e bastava premere perché
      quel perché — pensato per un'altra azione — finisse nel registro accessi
      dell'ente, che è append-only: quella riga non si corregge (AS7).
    */
    setReason('');
    setReasonMissing(false);
    setAsking(what);
  };

  const cancelAsking = (): void => {
    pending.current = { focus: openerRef.current };
    setAsking(undefined);
    setReason('');
    setReasonMissing(false);
  };

  /**
   * `Esc` chiude la conferma, come "Annulla".
   *
   * Passa dalla stessa funzione, così il ritorno del fuoco resta quello di
   * AJ16 e non nasce una seconda strada che un giorno diverge.
   */
  const onEscape = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Escape' || busy) return;
    event.stopPropagation();
    cancelAsking();
  };

  /**
   * Il perché, o niente.
   *
   * Annunciare "serve una motivazione" lasciando il fuoco sul pulsante appena
   * premuto vuol dire dire a chi non vede che manca qualcosa senza portarlo
   * dove manca. Qui il fuoco torna sul campo, che si segna anche come non
   * valido (WCAG 3.3.1). Prima il fuoco e poi l'annuncio (AP4): `focus()` è
   * sincrono, `say` programma un disegno che arriva dopo.
   */
  const requireReason = (): string | undefined => {
    const why = reason.trim();
    if (why) return why;
    setReasonMissing(true);
    reasonRef.current?.focus();
    say(t('platform.reason_required'), 'danger');
    return undefined;
  };

  /**
   * Eseguire l'intervento confermato.
   *
   * Le quattro chiamate hanno la stessa forma — guardia, motivo, esito,
   * fallimento, rilettura — e tenerle in quattro funzioni copiate era il modo
   * in cui una di loro un giorno avrebbe smesso di svuotare il campo o di
   * riportare il fuoco.
   */
  const confirm = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const current = asking;
    if (!current || busy) return;
    const why = requireReason();
    if (!why) return;
    setBusy(true);
    try {
      const said = await runAsking(current, why);
      pending.current = { focus: null, said, focusReissued: current.kind === 'invite' };
      setAsking(undefined);
      setReason('');
      await load();
    } catch (error) {
      say(`${t(failureKey(asking?.kind))} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ogni esito dice **anche quante conversazioni sono in corso**.
   *
   * Non è un dettaglio di cortesia: è il vincolo 5 reso visibile nell'unico
   * momento in cui qualcuno potrebbe credere di averle interrotte.
   */
  const runAsking = async (current: Asking, why: string): Promise<string> => {
    const live = (count: number): string =>
      count > 0 ? ` ${t('platform.live_sessions_note', { count })}` : '';

    if (current.kind === 'payment') {
      await setTenantStatus(id, { paymentStatus: current.status, reason: why });
      return t('platform.status_saved');
    }
    if (current.kind === 'suspension') {
      const result = await setTenantStatus(id, {
        newSessionsSuspended: current.suspend,
        reason: why,
      });
      return `${t('platform.status_saved')}${live(result.liveSessions)}`;
    }
    if (current.kind === 'activation') {
      const result = await setTenantActivation(id, { isActive: current.activate, reason: why });
      const text = t(result.isActive ? 'platform.activated' : 'platform.deactivated');
      return `${text}${live(result.liveSessions)}`;
    }
    if (current.kind === 'origin_add') {
      await addTenantOrigin(id, { origin: current.origin, reason: why });
      setOriginValue('');
      return t('platform.origin_added');
    }
    if (current.kind === 'origin_remove') {
      await removeTenantOrigin(id, current.originId, { reason: why });
      return t('platform.origin_removed');
    }
    if (current.kind === 'domain') {
      await setTenantDomain(id, { domain: current.domain, reason: why });
      return t(current.domain ? 'platform.domain_saved' : 'platform.domain_cleared');
    }
    if (current.kind === 'plan') {
      await setTenantPlan(id, {
        plan: current.plan,
        includedMinutes: current.includedMinutes,
        reason: why,
      });
      return t('platform.plan_saved');
    }
    const result = await reissueInvite(id, {
      email: current.email,
      role: current.role,
      reason: why,
    });
    setReissuedInvite({ ...result.invite, role: current.role });
    setInviteEmail('');
    return t('platform.invite_reissued');
  };

  const enter = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const why = enterReason.trim();
    if (!why || code.trim().length < 6 || busy) return;
    setBusy(true);
    try {
      await impersonate(id, { reason: why, code: code.trim() });
      /*
        La sessione è cambiata: da adesso questa scheda parla per conto
        dell'ente. Si ricarica la pagina invece di aggiornare lo stato — è il
        modo più semplice e il più onesto, perché tutto ciò che era in memoria
        apparteneva a un'altra sessione.
      */
      globalThis.location?.reload();
    } catch (error) {
      say(`${t('platform.impersonate_failed')} ${errorMessage(t, error)}`, 'danger');
      setBusy(false);
    }
  };

  /**
   * Registrare la cessazione. Stessa forma dell'accesso impersonato — motivo e
   * codice, pulsante spento finché mancano — perché è irreversibile come
   * quello è delicato. A differenza dell'ingresso, la sessione non cambia: si
   * rilegge la scheda e il fuoco torna al titolo, con l'esito.
   */
  const cease = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const why = ceaseReason.trim();
    if (why.length < 3 || ceaseCode.trim().length < 6 || busy) return;
    setBusy(true);
    try {
      const result = await ceaseTenant(id, { reason: why, code: ceaseCode.trim() });
      const live =
        result.liveSessions > 0
          ? ` ${t('platform.live_sessions_note', { count: result.liveSessions })}`
          : '';
      pending.current = { focus: null, said: `${t('platform.ceased_msg')}${live}` };
      setCeaseReason('');
      setCeaseCode('');
      await load();
    } catch (error) {
      say(`${t('platform.cease_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const when = (iso: string | null): string =>
    formatWhen(iso, i18n.resolvedLanguage, detail?.tenant.timezone);

  const tenant = detail?.tenant;
  /** Il nome dell'azione in corso: è il titolo della conferma e del pulsante. */
  const askingLabel = asking
    ? t(askingKey(asking), {
        domain: askingDomain(asking),
        origin: askingOrigin(asking),
        email: askingEmail(asking),
        role: askingRole(asking, t),
      })
    : '';

  return (
    <>
      <section className="card" aria-labelledby="tenant-heading">
        <div className="section-head">
          <h3 id="tenant-heading" ref={headingRef} tabIndex={-1}>
            {tenant?.name ?? t('platform.tenant_title')}
          </h3>
          {/*
            Il ritorno è servizio, non contenuto: `--quiet` e accanto al
            titolo, non un pulsante pieno in mezzo alla scheda dove aveva lo
            stesso peso di "Sospendi".
          */}
          <button type="button" className="button button--quiet" onClick={onBack}>
            {t('platform.back_to_tenants')}
          </button>
        </div>

        {announcer}

        {!detail && !failed ? <Loading label={t('admin.loading')} /> : null}

        {detail && tenant ? (
          <>
            {/*
              Lo stato amministrativo e quello del servizio, **scritti e in
              cima**. Erano due delle tredici coppie in fondo alla griglia, e
              sono le due che si viene a leggere: adesso stanno accanto al nome
              dell'ente, come parole e non come colori.
            */}
            <p className="cluster">
              <span
                className={
                  tenant.paymentStatus === 'current' ? 'badge badge--open' : 'badge badge--closed'
                }
              >
                {t(`platform.payment_${tenant.paymentStatus}`)}
              </span>
              <span className={tenant.isActive ? 'badge badge--open' : 'badge badge--closed'}>
                {t(tenant.isActive ? 'platform.state_active' : 'platform.state_inactive')}
              </span>
              {tenant.newSessionsSuspended ? (
                <span className="badge badge--closed">{t('platform.state_suspended')}</span>
              ) : null}
            </p>

            {/*
              Sei dati, non tredici.

              Gli altri sette non sono spariti: sono quelli che si guardano
              quando qualcosa non torna — il cluster, l'ultimo evento, i minuti
              per partecipante — e stanno dietro una riga che si apre. Tredici
              riquadri con lo stesso aspetto sono tredici cose che non si
              leggono: chi legge poco legge le prime.
            */}
            <dl className="facts">
              <div>
                <dt>{t('platform.fact_mode')}</dt>
                <dd>
                  {t(tenant.integrated ? 'platform.mode_integrated' : 'platform.mode_basic')}
                  {tenant.customDomain ? ` · ${tenant.customDomain}` : ''}
                </dd>
              </div>
              <div>
                <dt>{t('platform.fact_plan')}</dt>
                <dd>
                  {tenant.plan} · {tenant.includedMinutes}
                </dd>
              </div>
              <div>
                <dt>{t('platform.fact_minutes')}</dt>
                <dd>
                  {detail.usage.minutes} / {tenant.includedMinutes}
                </dd>
              </div>
              <div>
                <dt>{t('platform.fact_live')}</dt>
                <dd>{detail.technical.liveSessions}</dd>
              </div>
              <div>
                <dt>{t('platform.fact_volunteers')}</dt>
                <dd>{detail.technical.volunteers}</dd>
              </div>
              <div>
                <dt>{t('platform.fact_unanswered')}</dt>
                <dd>{detail.technical.unansweredWeek}</dd>
              </div>
            </dl>

            <details className="more">
              <summary>{t('platform.technical_details')}</summary>
              <dl className="facts">
                <div>
                  <dt>{t('platform.fact_slug')}</dt>
                  <dd>
                    <code>{tenant.slug}</code>
                  </dd>
                </div>
                <div>
                  <dt>{t('platform.fact_cluster')}</dt>
                  <dd>{tenant.cluster?.name ?? '—'}</dd>
                </div>
                <div>
                  {/*
                    I minuti per partecipante: la voce su cui paghiamo noi il
                    cluster (sezione 7). Sta qui e non nella schermata dell'ente,
                    perché non è un suo numero.
                  */}
                  <dt>{t('platform.fact_participant_minutes')}</dt>
                  <dd>{detail.usage.participantMinutes}</dd>
                </div>
                <div>
                  <dt>{t('platform.fact_otp')}</dt>
                  <dd>{detail.technical.otpMonth}</dd>
                </div>
                <div>
                  <dt>{t('platform.fact_last_webhook')}</dt>
                  <dd>{when(detail.technical.lastWebhookAt) || '—'}</dd>
                </div>
              </dl>
            </details>

            {detail.openInvites.length > 0 ? (
              <>
                <div className="section-head">
                  <div className="section-head__title">
                    <h4>{t('platform.invites_title')}</h4>
                    <InfoTip>{t('platform.invites_hint')}</InfoTip>
                  </div>
                  <span className="count">
                    {t('platform.count_invites', { count: detail.openInvites.length })}
                  </span>
                </div>
                <ul className="plain-list">
                  {detail.openInvites.map((invite) => (
                    <li key={invite.id} className={invite.expired ? 'row row--flagged' : 'row'}>
                      <div>
                        {/*
                          Il ruolo e la scadenza, non l'indirizzo: la domanda è
                          "il primo coordinatore è entrato?", e per rispondere non
                          serve sapere chi è.
                        */}
                        <p className="row__title">{t(`platform.role_${invite.role}`)}</p>
                        <p className="row__meta">
                          {invite.expired
                            ? t('platform.invite_expired')
                            : t('platform.invite_expires', { when: when(invite.expiresAt) })}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : null}
      </section>

      {detail && tenant ? (
        <>
          <section className="card" aria-labelledby="tenant-actions-heading">
            <div className="section-head__title">
              <h3 id="tenant-actions-heading">{t('platform.actions_title')}</h3>
              <InfoTip>{t('platform.actions_hint')}</InfoTip>
            </div>

            {asking ? (
              /*
                `<section>` e non `<div>`: è un riquadro con un titolo e un
                compito dentro, e da quando ascolta `Esc` deve essere un elemento
                a cui si possa attribuire un ruolo. Il titolo gli dà il nome,
                come nella moderazione e nella blacklist.
              */
              <section
                className="note"
                aria-labelledby="tenant-confirm-heading"
                onKeyDown={onEscape}
              >
                {/* Il titolo dice **cosa** si sta per fare: senza, si conferma alla cieca. */}
                <h4 id="tenant-confirm-heading" ref={askingHeadingRef} tabIndex={-1}>
                  {askingLabel}
                </h4>
                {/*
                  `noValidate`: senza, il `required` qui sotto fa bloccare la
                  submit dal browser con una nuvoletta nativa non tradotta e non
                  annunciata, e il gestore non viene nemmeno chiamato — il
                  pulsante sembra non fare niente. Il `required` resta perché è
                  quello che dice all'albero di accessibilità che il campo è
                  obbligatorio.
                */}
                <form onSubmit={(event) => void confirm(event)} noValidate>
                  <div className="field">
                    <label htmlFor="tenant-reason">{t('platform.reason_label')}</label>
                    <textarea
                      id="tenant-reason"
                      ref={reasonRef}
                      rows={2}
                      maxLength={500}
                      required
                      /*
                        Il messaggio d'errore è **legato al campo**, non solo
                        annunciato.

                        `aria-invalid` diceva "questo campo è sbagliato" e la
                        frase che spiega perché viveva in una regione live,
                        cioè in un punto che chi torna sul campo un minuto dopo
                        non ritrova più: lo screen reader annunciava "non
                        valido" e nient'altro. Elencando l'errore **prima**
                        dell'aiuto, chi ci porta il fuoco sente cosa manca e
                        poi come si compila (AS8).
                      */
                      aria-describedby={
                        reasonMissing
                          ? 'tenant-reason-error tenant-reason-hint'
                          : 'tenant-reason-hint'
                      }
                      aria-invalid={reasonMissing ? true : undefined}
                      value={reason}
                      onChange={(event) => {
                        setReason(event.target.value);
                        setReasonMissing(false);
                      }}
                    />
                    {reasonMissing ? (
                      <p className="alert" id="tenant-reason-error">
                        {t('platform.reason_required')}
                      </p>
                    ) : null}
                    <p className="muted" id="tenant-reason-hint">
                      {t('platform.reason_hint')}
                    </p>
                  </div>
                  {/*
                    Cambia etichetta mentre lavora, **non** si disabilita: il
                    fuoco è su questo pulsante nell'istante in cui si preme, e
                    disabilitarlo lo scaricherebbe sul `<body>` proprio mentre si
                    aspetta un esito (X3, U2). E non si disabilita nemmeno a
                    motivo vuoto: un pulsante disabilitato si attraversa in
                    silenzio, quindi sarebbe l'unico segnale del fatto che il
                    motivo serve — e non arriverebbe a chi non vede (AS5).

                    Il nome dice l'azione, non "Conferma": in una scheda che ne
                    apre quattro diverse, quattro pulsanti con lo stesso nome
                    sono quattro pulsanti indistinguibili per chi li scorre.
                  */}
                  <button type="submit" className="button button--primary">
                    {busy
                      ? t('platform.saving')
                      : t('platform.confirm_action', { action: askingLabel })}
                  </button>
                  <button type="button" className="button button--quiet" onClick={cancelAsking}>
                    {t('report_user.cancel')}
                  </button>
                </form>
              </section>
            ) : null}

            <h4>{t('platform.admin_state')}</h4>
            {/*
              Lo stato di adesso, scritto qui e non dedotto dal pulsante spento.
              Era l'unico modo per saperlo, e un pulsante disabilitato non lo
              dice a chi non vede.
            */}
            <p className="card__sub">
              {t('platform.payment_now', { state: t(`platform.payment_${tenant.paymentStatus}`) })}
            </p>
            <div className="actions">
              {(['current', 'due_soon', 'overdue'] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  className="button"
                  disabled={busy || tenant.paymentStatus === status}
                  onClick={(event) => ask(event, { kind: 'payment', status })}
                >
                  {t(`platform.set_payment_${status}`)}
                </button>
              ))}
            </div>
            <p className="note">{t('platform.payment_manual_note')}</p>

            <h4>{t('platform.fact_state')}</h4>
            {/*
              Sospensione e attivazione stanno insieme perché sono la stessa
              domanda a due gradi — "questo ente risponde?" — e separate da un
              titolo ciascuna sembravano due compiti senza rapporto.
            */}
            <div className="actions">
              <button
                type="button"
                className="button button--danger"
                disabled={busy}
                onClick={(event) =>
                  ask(event, { kind: 'suspension', suspend: !tenant.newSessionsSuspended })
                }
              >
                {t(
                  tenant.newSessionsSuspended
                    ? 'platform.resume_new_sessions'
                    : 'platform.suspend_new_sessions',
                )}
              </button>
            </div>
            <p className="note">{t('platform.suspension_hint')}</p>

            {/*
              Cosa manca prima che il widget si possa accendere (mock 4, `s-new`).

              Vale su un ente **nuovo**, non su uno già acceso: la regola è la
              condizione per accendere, non un cancello che si applica
              all'indietro — quello avrebbe spento il servizio di ogni ente
              esistente (AC9, AK2). Sta qui, attaccata al pulsante che accende,
              perché è l'unico posto in cui serve saperla.
            */}
            <div className="section-head__title">
              <h4>{t('platform.readiness_title')}</h4>
              <InfoTip>{t('platform.readiness_hint')}</InfoTip>
            </div>
            <ReadinessList readiness={detail.readiness} />
            <div className="actions">
              <button
                type="button"
                className={tenant.isActive ? 'button button--danger' : 'button'}
                disabled={
                  busy ||
                  (!tenant.isActive && (!detail.readiness.ready || tenant.ceasedAt !== null))
                }
                aria-describedby="tenant-activation-hint"
                onClick={(event) => ask(event, { kind: 'activation', activate: !tenant.isActive })}
              >
                {t(tenant.isActive ? 'platform.deactivate' : 'platform.activate')}
              </button>
            </div>
            <p className="note" id="tenant-activation-hint">
              {t(
                tenant.isActive
                  ? 'platform.deactivate_hint'
                  : tenant.ceasedAt
                    ? 'platform.ceased_no_activate'
                    : 'platform.activate_hint',
              )}
            </p>

            {/*
              "Diritto di proporre il passaggio di piano al rinnovo in caso di
              superamento stabile" (sezione 9, clausola 5): il contratto lo
              prevede, questa è la rotta che lo rende verificabile. Stesso
              disegno del dominio qui sotto — due campi persistenti e un
              pulsante che apre la conferma con il valore già scelto.
            */}
            <h4>{t('platform.plan_title')}</h4>
            <div className="grid-auto">
              <div className="field field--inline">
                <label htmlFor="tenant-plan">{t('platform.plan_label')}</label>
                <input
                  id="tenant-plan"
                  value={planValue}
                  maxLength={40}
                  onChange={(event) => setPlanValue(event.target.value)}
                />
              </div>
              <div className="field field--inline">
                <label htmlFor="tenant-plan-minutes">{t('platform.plan_minutes_label')}</label>
                <input
                  id="tenant-plan-minutes"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={minutesValue}
                  onChange={(event) => setMinutesValue(event.target.value)}
                />
              </div>
            </div>
            <div className="actions">
              <button
                type="button"
                className="button"
                disabled={
                  busy ||
                  !planValue.trim() ||
                  minutesValue.trim() === '' ||
                  Number.isNaN(Number(minutesValue)) ||
                  (planValue.trim() === tenant.plan &&
                    Number(minutesValue) === tenant.includedMinutes)
                }
                onClick={(event) =>
                  ask(event, {
                    kind: 'plan',
                    plan: planValue.trim(),
                    includedMinutes: Number(minutesValue),
                  })
                }
              >
                {t('platform.change_plan')}
              </button>
            </div>
            {/*
              "Cambiando il piano ora, i resoconti già emessi per mesi
              passati mostreranno il nuovo tetto se rigenerati": il prodotto
              non ha mai storicizzato il piano per mese, nemmeno per quello di
              partenza — non è un limite di questa sola azione, ma qui è
              dove va detto, perché è qui che si preme il pulsante
              (`docs/DECISIONI.md`, BB7).
            */}
            <p className="note">{t('platform.plan_hint')}</p>

            {/*
              I siti da cui il widget apre chiamate (sezione 4, BW6). Prima si
              scrivevano solo alla creazione dell'ente; il sottodominio della
              piattaforma resta sempre e non si toglie.
            */}
            <h4>{t('platform.origins_title')}</h4>
            <ul className="plain-list">
              {(detail.origins ?? []).map((row) => (
                <li key={row.id} className="row">
                  <div>
                    <p className="row__title">
                      <code>{row.origin}</code>
                    </p>
                    {row.isManaged ? (
                      <p className="row__meta">{t('platform.origins_managed')}</p>
                    ) : null}
                    {row.expiresAt ? (
                      <p className="row__meta">
                        {Date.parse(row.expiresAt) > Date.now()
                          ? t('platform.origin_expires', { when: when(row.expiresAt) })
                          : t('platform.origin_expired')}
                      </p>
                    ) : null}
                  </div>
                  {row.isManaged ? null : (
                    <div className="row__actions">
                      <button
                        type="button"
                        className="button"
                        disabled={busy}
                        aria-label={t('platform.origin_remove_named', { origin: row.origin })}
                        onClick={(event) =>
                          ask(event, {
                            kind: 'origin_remove',
                            originId: row.id,
                            origin: row.origin,
                          })
                        }
                      >
                        {t('platform.origin_remove')}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="field">
              <label htmlFor="tenant-origin">{t('platform.origin_label')}</label>
              <input
                id="tenant-origin"
                value={originValue}
                maxLength={200}
                autoComplete="off"
                inputMode="url"
                aria-describedby="tenant-origins-hint"
                onChange={(event) => setOriginValue(event.target.value)}
              />
            </div>
            <div className="actions">
              <button
                type="button"
                className="button"
                disabled={busy || originValue.trim() === ''}
                onClick={(event) => ask(event, { kind: 'origin_add', origin: originValue.trim() })}
              >
                {t('platform.origin_add')}
              </button>
            </div>
            <p className="note" id="tenant-origins-hint">
              {t('platform.origins_hint')}
            </p>

            {/*
              Il dominio custom decide la modalità (sezione 4): con un CNAME
              l'iframe è first-party e non si esce mai dal sito dell'ente; senza,
              si lavora in base, che è un funzionamento completo e non un ripiego.
            */}
            <h4>{t('platform.domain_title')}</h4>
            <div className="field">
              <label htmlFor="tenant-domain">{t('platform.domain_label')}</label>
              <input
                id="tenant-domain"
                value={domain}
                maxLength={253}
                autoComplete="off"
                onChange={(event) => setDomain(event.target.value.toLowerCase())}
              />
            </div>
            <div className="actions">
              <button
                type="button"
                className="button"
                disabled={busy || domain.trim() === (tenant.customDomain ?? '')}
                onClick={(event) => ask(event, { kind: 'domain', domain: domain.trim() || null })}
              >
                {t(domain.trim() ? 'platform.domain_save' : 'platform.domain_clear')}
              </button>
            </div>
            {tenant.customDomain ? (
              <p className="row__meta">
                {[
                  t(`platform.domain_status_${tenant.domainStatus ?? 'pending'}`),
                  t('platform.domain_checked', { when: when(tenant.domainCheckedAt) || '—' }),
                ].join(' · ')}
              </p>
            ) : null}
            <p className="note">{t('platform.domain_hint')}</p>

            {/*
              Rifare l'invito quando il collegamento originale è andato
              perso: la conferma dopo la creazione lo mostra una volta sola
              (AK3), e senza questo pulsante un ente creato per errore di
              distrazione — pagina chiusa troppo presto, scheda perduta —
              restava senza nessun modo di entrarci, tranne `psql` (AM6).
              La rotta chiude da sé gli inviti aperti dello stesso ruolo:
              il vecchio collegamento smette subito di valere.
            */}
            <div className="section-head__title">
              <h4>{t('platform.invite_reissue_title')}</h4>
              <InfoTip>{t('platform.invite_reissue_hint')}</InfoTip>
            </div>
            <div className="grid-auto">
              <div className="field field--inline">
                <label htmlFor="tenant-invite-email">
                  {t('platform.invite_reissue_email_label')}
                </label>
                <input
                  id="tenant-invite-email"
                  type="email"
                  value={inviteEmail}
                  maxLength={200}
                  onChange={(event) => setInviteEmail(event.target.value)}
                />
              </div>
              <div className="field field--inline">
                <label htmlFor="tenant-invite-role">
                  {t('platform.invite_reissue_role_label')}
                </label>
                <select
                  id="tenant-invite-role"
                  value={inviteRole}
                  onChange={(event) => setInviteRole(event.target.value as typeof inviteRole)}
                >
                  <option value="coordinator">{t('platform.role_coordinator')}</option>
                  <option value="supervisor">{t('platform.role_supervisor')}</option>
                  <option value="volunteer">{t('platform.role_volunteer')}</option>
                </select>
              </div>
            </div>
            <div className="actions">
              {/*
                Spento anche a riquadro aperto: il collegamento appena
                generato non si rilegge (AK3, come alla creazione), quindi
                un secondo reinvito prima di "Ho copiato il collegamento" lo
                farebbe sparire senza avviso, bruciando anche quello nuovo —
                lo chiude subito il server (`superadmin.ts:676-686`), non
                solo lo schermo.
              */}
              <button
                type="button"
                className="button"
                disabled={busy || !inviteEmail.trim() || reissuedInvite !== null}
                onClick={(event) =>
                  ask(event, { kind: 'invite', role: inviteRole, email: inviteEmail.trim() })
                }
              >
                {t('platform.reissue_invite')}
              </button>
            </div>

            {reissuedInvite ? (
              <section className="note" aria-labelledby="tenant-reissued-invite-heading">
                {/*
                  Non `new_invite_title`/`new_invite_hint`: quelle parlano
                  sempre "del primo coordinatore", ma qui il ruolo è quello
                  scelto nel modulo sopra — spesso un supervisore o un
                  volontario, e quasi mai il primo invito dell'ente.
                */}
                <h4 id="tenant-reissued-invite-heading" ref={reissuedRef} tabIndex={-1}>
                  {t('platform.invite_reissue_new_title', {
                    role: t(`platform.role_${reissuedInvite.role}`),
                  })}
                </h4>
                <p className="card__sub">
                  {t('platform.invite_reissue_new_hint', { email: reissuedInvite.email })}
                </p>
                <p className="secret">
                  <code>{reissuedInvite.url}</code>
                </p>
                <p className="banner banner--warn">{t('platform.new_invite_once')}</p>
                <div className="actions">
                  <button type="button" className="button" onClick={() => setReissuedInvite(null)}>
                    {t('platform.invite_reissue_copied')}
                  </button>
                </div>
              </section>
            ) : null}
          </section>

          {/*
            L'accesso impersonato, e sta da solo (sezione 9bis).

            È la cosa più delicata del pannello: si entra nei dati di un ente
            vero. Una scheda sua, in fondo alla pagina, con il proprio motivo
            obbligatorio e il proprio secondo fattore — non un quinto gruppo di
            pulsanti accanto a "Segna in regola". Non è diventato più facile:
            servono ancora un perché e un codice, il pulsante resta spento
            finché non ci sono entrambi, e la riga rossa dice che l'ente lo
            vedrà. È diventato più difficile da premere per sbaglio, che è
            l'unica direzione in cui questo comando può muoversi.
          */}
          <section className="card" aria-labelledby="tenant-enter-heading">
            <h3 id="tenant-enter-heading">{t('platform.impersonate_title')}</h3>
            <p className="banner banner--warn">{t('platform.impersonate_visible_note')}</p>
            <form onSubmit={(event) => void enter(event)}>
              <div className="field">
                <label htmlFor="impersonate-reason">{t('platform.reason_label')}</label>
                <textarea
                  id="impersonate-reason"
                  rows={2}
                  maxLength={500}
                  required
                  aria-describedby="impersonate-hint"
                  value={enterReason}
                  onChange={(event) => setEnterReason(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="impersonate-code">{t('platform.totp_label')}</label>
                <input
                  id="impersonate-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={10}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
              {/*
                Qui il pulsante **resta** disabilitato finché mancano il motivo
                o il codice, contro la regola che vale in tutto il resto della
                console. È una scelta, non una svista: altrove un pulsante
                spento è l'unico segnale di ciò che manca e va tolto, qui la
                frase sopra lo dice a parole — ed è collegata al pulsante con
                `aria-describedby`, quindi la sente anche chi non vede — mentre
                un pulsante sempre premibile su questa azione sarebbe un modo in
                più di entrare per sbaglio nei dati di qualcun altro.
              */}
              <p className="note" id="impersonate-hint">
                {t('platform.impersonate_hint')}
              </p>
              <button
                type="submit"
                className="button button--primary"
                aria-describedby="impersonate-hint"
                disabled={busy || enterReason.trim().length < 3 || code.trim().length < 6}
              >
                {t('platform.impersonate_enter', { name: tenant.name })}
              </button>
            </form>
          </section>

          {/*
            La cessazione del rapporto (sezione 7quinquies, BW3), ultima di
            tutte: è l'unica azione della scheda che non si annulla. Motivo e
            secondo fattore come per l'ingresso, e per la stessa ragione il
            pulsante resta spento finché mancano. Fa partire la prescrizione
            decennale delle copie firmate; non esporta e non cancella i dati
            (pipeline non ancora costruita, detto nel testo sotto).
          */}
          <section className="card" aria-labelledby="tenant-cease-heading">
            <h3 id="tenant-cease-heading">{t('platform.cease_title')}</h3>
            {tenant.ceasedAt ? (
              <p className="banner banner--warn">
                {t('platform.ceased_on', { when: when(tenant.ceasedAt) })}
              </p>
            ) : (
              <form onSubmit={(event) => void cease(event)}>
                <p className="banner banner--warn" id="cease-explain">
                  {t('platform.cease_explain')}
                </p>
                <div className="field">
                  <label htmlFor="cease-reason">{t('platform.reason_label')}</label>
                  <textarea
                    id="cease-reason"
                    rows={2}
                    maxLength={500}
                    required
                    aria-describedby="cease-hint"
                    value={ceaseReason}
                    onChange={(event) => setCeaseReason(event.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="cease-code">{t('platform.totp_label')}</label>
                  <input
                    id="cease-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={10}
                    value={ceaseCode}
                    onChange={(event) => setCeaseCode(event.target.value)}
                  />
                </div>
                <p className="note" id="cease-hint">
                  {t('platform.cease_hint')}
                </p>
                <button
                  type="submit"
                  className="button button--danger"
                  aria-describedby="cease-explain cease-hint"
                  disabled={busy || ceaseReason.trim().length < 3 || ceaseCode.trim().length < 6}
                >
                  {t('platform.cease_confirm', { name: tenant.name })}
                </button>
              </form>
            )}
          </section>
        </>
      ) : null}
    </>
  );
}

/** Il nome dell'azione che si sta confermando: uno per forma di conferma. */
function askingKey(asking: Asking): string {
  if (asking.kind === 'payment') return `platform.set_payment_${asking.status}`;
  if (asking.kind === 'suspension') {
    return asking.suspend ? 'platform.suspend_new_sessions' : 'platform.resume_new_sessions';
  }
  if (asking.kind === 'activation') {
    return asking.activate ? 'platform.activate' : 'platform.deactivate';
  }
  if (asking.kind === 'domain') {
    return asking.domain ? 'platform.domain_save' : 'platform.domain_clear';
  }
  if (asking.kind === 'origin_add') return 'platform.origin_add_confirm';
  if (asking.kind === 'origin_remove') return 'platform.origin_remove_named';
  /*
    Non `platform.reissue_invite`: quella è la sola etichetta del pulsante
    che apre la conferma. Il titolo della conferma è il posto in cui chi
    firma un'azione che chiude subito il vecchio invito dello stesso ruolo
    (`superadmin.ts:676-686`) deve poter leggere a chi va e per quale ruolo,
    non solo "Rifai l'invito" — specie per chi arriva col fuoco e sente solo
    il titolo, come nel dominio qui sotto.
  */
  if (asking.kind === 'invite') return 'platform.reissue_invite_confirm';
  return 'platform.change_plan';
}

function askingDomain(asking: Asking): string {
  return asking.kind === 'domain' ? (asking.domain ?? '') : '';
}

function askingOrigin(asking: Asking): string {
  return asking.kind === 'origin_add' || asking.kind === 'origin_remove' ? asking.origin : '';
}

function askingEmail(asking: Asking): string {
  return asking.kind === 'invite' ? asking.email : '';
}

function askingRole(asking: Asking, t: TFunction): string {
  return asking.kind === 'invite' ? t(`platform.role_${asking.role}`) : '';
}

/** Il messaggio di fallimento parla della cosa che non è riuscita, non "dello stato". */
function failureKey(kind: Asking['kind'] | undefined): string {
  if (kind === 'activation') return 'platform.activation_failed';
  if (kind === 'domain') return 'platform.domain_failed';
  if (kind === 'origin_add' || kind === 'origin_remove') return 'platform.origin_failed';
  if (kind === 'plan') return 'platform.plan_failed';
  if (kind === 'invite') return 'platform.invite_reissue_failed';
  return 'platform.status_failed';
}

// ------------------------------------------------------------- ente nuovo

/**
 * Creare un ente (mock 4, `s-new`).
 *
 * "I primi tenant si creano da qui, a mano." Quattro campi a sinistra, e a
 * destra l'elenco di cosa succede alla creazione: la metà automatica e la metà
 * "da compilare". Non è decorazione — è la regola resa visibile: **il widget
 * non si attiva finché protocollo e orari non ci sono**, e l'ente nasce spento
 * proprio per questo.
 *
 * I nove campi erano in fila, tutti uguali, con un invio in fondo: un modulo
 * senza articolazioni si compila guardando il campo che si sta scrivendo e
 * niente altro. Adesso sono quattro gruppi con un titolo che dice a quale
 * domanda rispondono — chi è l'ente, cosa ha comprato, dove si trova, chi lo
 * apre — e ognuno si può finire prima di passare al successivo.
 *
 * Una cosa che questa schermata non fa: non chiede nessun dato di pagamento,
 * perché nel prodotto non esiste nessuna integrazione di pagamento (sezione
 * 9). Il collegamento dell'invito compare qui, una volta sola, e lo consegna
 * chi ha creato l'ente (AK3) — un'email parte anche in automatico al
 * coordinatore invitato, ma best-effort: se non arriva, il collegamento
 * mostrato qui resta il modo che non fallisce mai.
 */
function NewTenantSection({
  headingRef,
  onCreated,
}: {
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  onCreated: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { say, announcer } = useAnnounce();
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedTenant>();
  /**
   * Niente valori del prodotto precompilati.
   *
   * Il piano e i minuti inclusi sono il contratto, il paese decide i numeri di
   * emergenza non rimuovibili: sono tre decisioni, e una decisione presa da un
   * valore predefinito è una decisione che nessuno ha preso. Sarebbero anche
   * tre soglie scritte nel sorgente, che `CLAUDE.md` vieta.
   *
   * Restano il fuso e la lingua, che non sono valori del prodotto ma **dove si
   * trova chi sta compilando**: il primo dal browser, la seconda dalla lingua
   * della console.
   */
  const [form, setForm] = useState({
    name: '',
    slug: '',
    coordinatorEmail: '',
    plan: '',
    includedMinutes: '',
    countryCode: '',
    timezone: defaultTimeZone(),
    defaultLocale: i18n.resolvedLanguage ?? 'it',
    siteUrl: '',
  });
  const doneRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (created) doneRef.current?.focus();
  }, [created]);

  const set = (key: keyof typeof form, value: string): void =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const result = await createTenant({
        name: form.name.trim(),
        slug: form.slug.trim(),
        coordinatorEmail: form.coordinatorEmail.trim(),
        plan: form.plan.trim(),
        includedMinutes: Number(form.includedMinutes),
        countryCode: form.countryCode.trim(),
        timezone: form.timezone.trim(),
        defaultLocale: form.defaultLocale.trim(),
        ...(form.siteUrl.trim() ? { siteUrl: form.siteUrl.trim() } : {}),
      });
      setCreated(result);
      say(t('platform.new_created', { name: result.tenant.name }));
    } catch (error) {
      say(`${t('platform.new_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <section className="card" aria-labelledby="new-done-heading">
        <h3 id="new-done-heading" ref={doneRef} tabIndex={-1}>
          {t('platform.new_done_title', { name: created.tenant.name })}
        </h3>
        {announcer}

        <dl className="facts">
          <div>
            <dt>{t('platform.fact_slug')}</dt>
            <dd>
              <code>{created.tenant.slug}</code>
            </dd>
          </div>
          <div>
            <dt>{t('platform.new_fact_site_key')}</dt>
            <dd>
              <code>{created.tenant.siteKey}</code>
            </dd>
          </div>
          <div>
            <dt>{t('platform.new_fact_origin')}</dt>
            <dd>
              <code>{created.tenant.origin}</code>
            </dd>
          </div>
        </dl>

        {/*
          Il collegamento dell'invito, **una volta sola**.

          In tabella da adesso c'è solo l'hash: se questa schermata si chiude,
          l'invito si rifà, non si rilegge (AK3) — anche se un'email è partita
          in automatico verso il coordinatore, best-effort e non garantita.
        */}
        <h4>{t('platform.new_invite_title')}</h4>
        <p className="card__sub">
          {t('platform.new_invite_hint', { email: created.invite.email })}
        </p>
        <p className="secret">
          <code>{created.invite.url}</code>
        </p>
        <p className="banner banner--warn">{t('platform.new_invite_once')}</p>

        <div className="section-head__title">
          <h4>{t('platform.readiness_title')}</h4>
          <InfoTip>{t('platform.readiness_hint')}</InfoTip>
        </div>
        <ReadinessList readiness={created.readiness} />

        <div className="actions">
          <button type="button" className="button button--primary" onClick={onCreated}>
            {t('platform.new_back_to_tenants')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="card" aria-labelledby="new-heading">
      <div className="section-head__title">
        <h3 id="new-heading" ref={headingRef} tabIndex={-1}>
          {t('platform.new_title')}
        </h3>
        <InfoTip>{t('platform.new_hint')}</InfoTip>
      </div>
      {/*
        `{announcer}` **subito dopo il titolo**, come nel ramo qui sopra.

        I due rami di questa sezione hanno la stessa radice, quindi React
        confronta i figli per posizione: con il testo introduttivo in mezzo,
        `{announcer}` stava all'indice 2 di qua e all'indice 1 di là, e creando
        un ente React trovava un `p` dove adesso c'è un frammento — distruggeva
        la regione live e ne montava una nuova **con il messaggio già dentro**.
        Che è AM4 daccapo, sul ramo opposto: AM4 aveva corretto l'errore, e chi
        non vede continuava a non sentire il nome dell'ente appena creato né che
        resta spento (WCAG 4.1.3, `docs/DECISIONI.md`, AO9). Il titolo è
        diventato un `<div>` con dentro anche l'`InfoTip.tsx` della scheda, ma
        resta comunque il primo figlio della sezione su entrambi i rami:
        `{announcer}` resta al secondo posto qui come di là.
      */}
      {announcer}

      <form onSubmit={(event) => void submit(event)}>
        <h4>{t('platform.new_group_org')}</h4>
        <div className="field">
          <label htmlFor="new-name">{t('platform.new_name')}</label>
          <input
            id="new-name"
            value={form.name}
            maxLength={120}
            required
            onChange={(event) => set('name', event.target.value)}
          />
        </div>

        <div className="field">
          {/*
            Non modificabile dopo: finisce nel sottodominio, e il sottodominio
            finisce nello snippet incollato sul sito dell'ente e nei certificati
            già emessi. Il database lo impedisce, non solo questa frase.

            A campo vuoto la frase non contiene un trattino.

            `{{slug}}` è un esempio di quello che si sta scrivendo, e finché non
            si è scritto niente non c'è nessun esempio: "Determina il
            sottodominio e la pagina di video e chat: —." è un segnaposto
            tecnico finito in mezzo a una frase, sulla prima schermata che si
            apre per creare un ente.
          */}
          <div className="field__label-row">
            <label htmlFor="new-slug">{t('platform.new_slug')}</label>
            <FieldHint id="new-slug-hint" label={t('platform.new_slug')}>
              {form.slug
                ? t('platform.new_slug_hint', { slug: form.slug })
                : t('platform.new_slug_hint_empty')}
            </FieldHint>
          </div>
          <input
            id="new-slug"
            value={form.slug}
            maxLength={40}
            required
            aria-describedby="new-slug-hint"
            onChange={(event) => set('slug', event.target.value.toLowerCase())}
          />
        </div>

        <div className="field">
          <div className="field__label-row">
            <label htmlFor="new-site">{t('platform.new_site')}</label>
            <FieldHint id="new-site-hint" label={t('platform.new_site')}>
              {t('platform.new_site_hint')}
            </FieldHint>
          </div>
          <input
            id="new-site"
            type="url"
            value={form.siteUrl}
            maxLength={200}
            aria-describedby="new-site-hint"
            onChange={(event) => set('siteUrl', event.target.value)}
          />
        </div>

        <h4>{t('platform.new_group_contract')}</h4>
        {/*
          Due campi affiancati, e non dentro `.filters`: quella striscia grigia
          è la barra che governa un elenco, e usarla qui diceva a chi guarda
          che piano e minuti fossero un filtro. `.grid-auto` li mette accanto
          finché ci stanno e uno sotto l'altro quando non ci stanno, che su un
          telefono è l'unica disposizione leggibile.
        */}
        <div className="grid-auto">
          <div className="field field--inline">
            {/*
              Un suggerimento, non un elenco chiuso: la tabella tenant non ha
              un enum di piani (sezione 3) e il modello di prezzo resta da
              definire (`docs/DECISIONI.md`). `<datalist>` propone qualche
              nome ragionevole ma il campo scrive comunque testo libero, così
              un ente con un accordo diverso da quelli suggeriti non resta
              senza opzione valida (AM11: un valore precompilato è una
              decisione presa da nessuno).

              Il supporto di `datalist` a leggere i suggerimenti è incoerente
              fra le combinazioni screen reader/browser (es. NVDA+Chrome non
              sempre lo annuncia): l'aiuto collegato con `aria-describedby`
              come `new-slug` e `new-site` garantisce che l'esistenza dei
              suggerimenti arrivi comunque a chi non vede, indipendentemente
              da quel supporto nativo — resta sempre nell'albero di
              accessibilità anche dietro il pulsante (i) (`FieldHint.tsx`).
            */}
            <div className="field__label-row">
              <label htmlFor="new-plan">{t('platform.new_plan')}</label>
              <FieldHint id="new-plan-hint" label={t('platform.new_plan')}>
                {t('platform.new_plan_hint')}
              </FieldHint>
            </div>
            <input
              id="new-plan"
              list="new-plan-options"
              value={form.plan}
              maxLength={40}
              required
              aria-describedby="new-plan-hint"
              onChange={(event) => set('plan', event.target.value)}
            />
            <datalist id="new-plan-options">
              <option value={t('platform.plan_option_base')} />
              <option value={t('platform.plan_option_standard')} />
              <option value={t('platform.plan_option_advanced')} />
              <option value={t('platform.plan_option_custom')} />
            </datalist>
          </div>
          <div className="field field--inline">
            <label htmlFor="new-minutes">{t('platform.new_minutes')}</label>
            <input
              id="new-minutes"
              type="number"
              inputMode="numeric"
              min={0}
              value={form.includedMinutes}
              required
              onChange={(event) => set('includedMinutes', event.target.value)}
            />
          </div>
        </div>

        <h4>{t('platform.new_group_place')}</h4>
        <div className="grid-auto">
          <div className="field field--inline">
            {/*
              L'aiuto sta accanto al campo che descrive, non lontano da lui:
              collegato per l'assistiva ma altrove per chi vede è la stessa
              informazione data a metà (WCAG 1.3.2) — per questo il pulsante
              (i) sta sulla stessa riga del campo, non sopra o sotto di lui.
            */}
            <div className="field__label-row">
              <label htmlFor="new-country">{t('platform.new_country')}</label>
              <FieldHint id="new-country-hint" label={t('platform.new_country')}>
                {t('platform.new_country_hint')}
              </FieldHint>
            </div>
            <input
              id="new-country"
              value={form.countryCode}
              maxLength={2}
              required
              aria-describedby="new-country-hint"
              onChange={(event) => set('countryCode', event.target.value.toUpperCase())}
            />
          </div>
          <div className="field field--inline">
            <label htmlFor="new-timezone">{t('platform.new_timezone')}</label>
            <input
              id="new-timezone"
              value={form.timezone}
              maxLength={64}
              required
              onChange={(event) => set('timezone', event.target.value)}
            />
          </div>
          <div className="field field--inline">
            <label htmlFor="new-locale">{t('platform.new_locale')}</label>
            <select
              id="new-locale"
              value={form.defaultLocale}
              onChange={(event) => set('defaultLocale', event.target.value)}
            >
              {SUPPORTED_LOCALES.map((locale) => (
                <option key={locale} value={locale}>
                  {t(`language.${locale}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <h4>{t('platform.new_group_contact')}</h4>
        <div className="field">
          <div className="field__label-row">
            <label htmlFor="new-email">{t('platform.new_email')}</label>
            <FieldHint id="new-email-hint" label={t('platform.new_email')}>
              {t('platform.new_email_hint')}
            </FieldHint>
          </div>
          <input
            id="new-email"
            type="email"
            inputMode="email"
            autoComplete="off"
            value={form.coordinatorEmail}
            maxLength={200}
            required
            aria-describedby="new-email-hint"
            onChange={(event) => set('coordinatorEmail', event.target.value)}
          />
        </div>

        <h4>{t('platform.new_what_happens')}</h4>
        <ul className="plain-list">
          {(['tenant', 'subdomain', 'invite'] as const).map((step) => (
            <li key={step} className="row">
              <p>{t(`platform.new_auto_${step}`)}</p>
              <span className="badge badge--open">{t('platform.new_automatic')}</span>
            </li>
          ))}
          {READINESS_STEPS.map((step) => (
            <li key={step.field} className="row">
              <p>{t(step.labelKey)}</p>
              <span className="badge badge--closed">{t('platform.new_to_fill')}</span>
            </li>
          ))}
        </ul>
        <p className="banner banner--warn">{t('platform.new_gate_note')}</p>
        {/* Sezione 9: nessuna integrazione di pagamento, per nessun motivo. */}
        <p className="note">{t('platform.new_no_payment_note')}</p>

        <div className="actions">
          <button type="submit" className="button button--primary" disabled={busy}>
            {t('platform.new_submit')}
          </button>
        </div>
      </form>
    </section>
  );
}

/** Le tre condizioni per accendere un ente, e quali mancano. */
function ReadinessList({ readiness }: { readiness: TenantReadiness }) {
  const { t } = useTranslation();
  return (
    <ul className="plain-list">
      {READINESS_STEPS.map((step) => (
        <li key={step.field} className={readiness[step.field] ? 'row' : 'row row--flagged'}>
          <p>{t(step.labelKey)}</p>
          <span className={readiness[step.field] ? 'badge badge--open' : 'badge badge--closed'}>
            {t(readiness[step.field] ? 'platform.readiness_done' : 'platform.readiness_missing')}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * L'ordine in cui la schermata elenca le condizioni, e come si chiamano.
 *
 * La chiave della traduzione è scritta per esteso e non composta dal nome del
 * campo: una chiave costruita a pezzi non la trova il test che verifica che
 * ogni chiave usata esista in tutte le lingue, e sparirebbe in silenzio il
 * giorno in cui una lingua non ce l'ha.
 */
const READINESS_STEPS = [
  { field: 'emergencyNumbers', labelKey: 'platform.readiness_emergency_numbers' },
  { field: 'crisisProtocol', labelKey: 'platform.readiness_crisis_protocol' },
  { field: 'openingHours', labelKey: 'platform.readiness_opening_hours' },
  { field: 'art28Signed', labelKey: 'platform.readiness_art28_signed' },
] as const satisfies readonly { field: keyof TenantReadiness; labelKey: string }[];

/** Il fuso di chi sta guardando: un valore di partenza, non una decisione. */
function defaultTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ------------------------------------------------------ domini e certificati

/**
 * Domini e certificati (mock 4, `s-domains`).
 *
 * "Rilevante solo per gli enti in modalità integrata. Il rinnovo dei
 * certificati è automatico, ma i fallimenti vanno visti."
 *
 * La frase che questa schermata deve dire più di ogni altra è quella in fondo
 * al mock: **il servizio non si è mai interrotto**. Un CNAME che smette di
 * risolvere fa tornare il widget alla modalità base, che è come lavora chi un
 * dominio non l'ha mai configurato — non è un guasto del servizio, è una
 * configurazione che è cambiata.
 */
function DomainsSection({
  headingRef,
}: {
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const { say, announcer } = useAnnounce();
  const [busy, setBusy] = useState(false);

  const read = useCallback((signal: AbortSignal) => platformDomains({ signal }), []);
  const { data, failed, reload } = useLoad(read, {
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const verify = async (row: DomainRow): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await verifyTenantDomain(row.tenantId);
      /*
        Una verifica **non riuscita** non è un dominio che non risolve: lo stato
        di prima resta in piedi, e dirlo è l'unico modo perché chi ha premuto il
        pulsante non creda di avere un dato fresco (AK4).
      */
      say(
        result.checked
          ? t(`platform.domain_verified_${result.status}`, {
              domain: row.domain ?? '',
              target: result.target ?? '—',
            })
          : t('platform.domain_verify_unknown', { domain: row.domain ?? '' }),
      );
      await reload();
    } catch (error) {
      say(`${t('platform.domain_verify_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const when = (iso: string | null): string =>
    iso ? formatWhen(iso, i18n.resolvedLanguage, undefined) : t('platform.never');

  const unresolved = data?.domains.filter((row) => row.status === 'unresolved').length ?? 0;

  return (
    <section className="card" aria-labelledby="domains-heading">
      <div className="section-head">
        <div className="section-head__title">
          <h3 id="domains-heading" ref={headingRef} tabIndex={-1}>
            {t('platform.domains_title')}
          </h3>
          <InfoTip>{t('platform.domains_hint')}</InfoTip>
        </div>
        {data ? (
          <span className="count">
            {t('platform.count_domains', { count: data.domains.length })}
          </span>
        ) : null}
      </div>
      {announcer}

      {/*
        Il record da configurare governa tutta la sezione: sta sopra l'elenco,
        dove lo si cerca, e non fra le note in fondo.
      */}
      {data ? <p className="note">{t('platform.domains_cname', { host: data.edgeHost })}</p> : null}

      {unresolved > 0 ? (
        <p className="banner banner--warn">
          {t('platform.domains_unresolved_count', { count: unresolved })}
        </p>
      ) : null}

      {!data && !failed ? <Loading label={t('admin.loading')} /> : null}
      {data && data.domains.length === 0 ? <Empty text={t('platform.domains_empty')} /> : null}

      <ul className="plain-list">
        {data?.domains.map((row) => (
          <li
            key={row.tenantId}
            className={row.status === 'unresolved' ? 'row row--flagged' : 'row'}
          >
            <div className="stack stack--tight">
              <p className="row__title">{row.tenant}</p>
              <p className="row__meta">
                <code>{row.domain}</code>
              </p>
              {/*
                Il record per esteso, non solo il bersaglio: chi lo deve
                correggere lo copia da qui invece di ricostruirlo (mock 5,
                `o-tech`).
              */}
              {row.cname ? (
                <p className="row__meta">
                  <code>{`${row.cname.name} ${row.cname.type} ${row.cname.value}`}</code>
                </p>
              ) : null}
              <p className="row__meta">
                {[
                  t(`platform.domain_status_${row.status}`),
                  t('platform.domain_checked', { when: when(row.checkedAt) }),
                  row.certificateExpiresAt
                    ? t('platform.domain_certificate', { when: when(row.certificateExpiresAt) })
                    : t('platform.domain_certificate_none'),
                ].join(' · ')}
              </p>
              {row.status === 'unresolved' ? (
                /*
                  La frase del mock, e non è consolatoria: il widget è servito
                  in modalità base come fallback, e nessuna conversazione è
                  stata interrotta. È una conseguenza, non un dato della riga:
                  ha la forma della nota, non quella dei dati sopra.
                */
                <p className="note">
                  {t('platform.domain_fallback_note', { since: when(row.unresolvedSince) })}
                </p>
              ) : null}
            </div>
            <div className="row__actions">
              <button
                type="button"
                className="button"
                disabled={busy}
                aria-label={t('platform.domain_verify', { domain: row.domain ?? '' })}
                onClick={() => void verify(row)}
              >
                {t('platform.domain_verify_short')}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <p className="note">{t('platform.domains_footer')}</p>
    </section>
  );
}

// ------------------------------------------------------------------ avvisi

function AlertsSection({ headingRef }: { headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState('open');
  const [busy, setBusy] = useState(false);
  const { say, announcer } = useAnnounce();

  const read = useCallback((signal: AbortSignal) => platformAlerts(state, { signal }), [state]);
  const {
    data,
    failed,
    reload: load,
  } = useLoad(read, {
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });
  const alerts = data?.alerts;

  const handle = async (id: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await handleAlert(id);
      say(t('platform.alert_handled'));
      await load();
    } catch (error) {
      say(`${t('platform.alert_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="alerts-heading">
      <div className="section-head">
        <div className="section-head__title">
          <h3 id="alerts-heading" ref={headingRef} tabIndex={-1}>
            {t('platform.alerts_title')}
          </h3>
          <InfoTip>{t('platform.alerts_hint')}</InfoTip>
        </div>
        {alerts ? (
          <span className="count">{t('platform.count_alerts', { count: alerts.length })}</span>
        ) : null}
      </div>

      {announcer}

      <div className="filters">
        <div className="field field--inline">
          <label htmlFor="alerts-state">{t('platform.filter_state')}</label>
          <select
            id="alerts-state"
            value={state}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="open">{t('platform.state_open')}</option>
            <option value="handled">{t('platform.state_handled')}</option>
            <option value="all">{t('platform.state_all')}</option>
          </select>
        </div>
      </div>

      {!alerts && !failed ? <Loading label={t('admin.loading')} /> : null}
      {alerts && alerts.length === 0 ? <Empty text={t('platform.alerts_empty')} /> : null}

      <ul className="plain-list">
        {alerts?.map((alert) => (
          <li
            key={alert.id}
            /*
              "Le chiamate senza risposta sono l'avviso più importante:
              significa che l'ente ha turni scoperti e che persone reali non
              hanno ricevuto risposta" (mock 4). Un avviso che riguarda una
              cancellazione mancata è della stessa famiglia: entrambi sono
              obblighi non rispettati, non guasti tecnici.
            */
            className={URGENT_ALERTS.has(alert.kind) ? 'row row--flagged' : 'row'}
          >
            <div className="stack stack--tight">
              <p className="row__title">{t(`platform.alert_${alert.kind}`)}</p>
              <p className="row__meta">
                {[
                  alert.tenant ?? t('platform.alert_platform_wide'),
                  formatWhen(alert.createdAt, i18n.resolvedLanguage, undefined),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {alert.detail ? (
                <p className="row__meta">{describeAlert(t, alert.kind, alert.detail)}</p>
              ) : null}
              {/* "Nel dubbio si parla con l'ente, non si segnala nessuno" (9quater). */}
              {ALERT_NOTES[alert.kind] ? (
                <p className="note">{t(ALERT_NOTES[alert.kind] as string)}</p>
              ) : null}
              {alert.handledAt ? (
                <p className="row__meta">
                  {t('platform.alert_handled_by', {
                    name: alert.handledBy ?? '—',
                    when: formatWhen(alert.handledAt, i18n.resolvedLanguage, undefined),
                  })}
                </p>
              ) : null}
            </div>
            {alert.handledAt ? null : (
              <div className="row__actions">
                {/*
                  A schermo la parola breve, per esteso nel nome accessibile:
                  "Segna come gestito" ripetuto quaranta volte non dice a chi
                  scorre i pulsanti quale avviso sta chiudendo (WCAG 2.4.6).
                */}
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  aria-label={t('platform.alert_handle_named', {
                    alert: t(`platform.alert_${alert.kind}`),
                  })}
                  onClick={() => void handle(alert.id)}
                >
                  {t('platform.alert_handle')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="note">{t('platform.alerts_footer')}</p>
    </section>
  );
}

/**
 * Avvisi che dicono che una promessa non è stata mantenuta, non che qualcosa è
 * lento.
 *
 * `unanswered_calls` sta in testa perché è quello che il mock 4 chiama "il più
 * importante": persone reali non hanno ricevuto risposta. Era l'unico a non
 * essere evidenziato, proprio sotto il commento che lo cita.
 */
const URGENT_ALERTS = new Set([
  'unanswered_calls',
  'custom_domain_unresolved',
  'retention_job_failed',
  'backup_failed',
  'cluster_unreachable',
  'usage_anomaly',
]);

/**
 * Gli avvisi che portano con sé **cosa farne**, oltre a cosa è successo.
 *
 * Sono quelli in cui la riga da sola sarebbe fraintesa: uno che non è una
 * prova, uno che va detto a una persona invece che archiviato, uno che sembra
 * un guasto del servizio e non lo è — e il backup, che non ferma niente oggi e
 * per questo si rimanda, finché il server non si guasta (BV2).
 */
const ALERT_NOTES: Readonly<Record<string, string>> = {
  usage_anomaly: 'platform.alert_usage_anomaly_note',
  unanswered_calls: 'platform.alert_unanswered_note',
  custom_domain_unresolved: 'platform.alert_domain_note',
  backup_failed: 'platform.alert_backup_note',
};

// -------------------------------------------------------- blacklist globale

/**
 * La blacklist globale (mock 4, `s-blacklist`).
 *
 * "Solo hash, mai numeri in chiaro. Blocca abusatori seriali su tutta la rete."
 *
 * Non c'è un campo in cui digitare un'impronta, e non è una limitazione: il
 * fornitore un numero non ce l'ha mai avuto. Si parte da un blocco che **un
 * ente ha già deciso**, e si estende. La colonna "da quanti enti" è lo schema
 * che una blacklist globale esiste per riconoscere.
 */
function BlacklistSection({
  headingRef,
}: {
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const [asking, setAsking] = useState<BanCandidate>();
  /**
   * Il blocco che si sta per **togliere**.
   *
   * Anche togliere chiede un perché: è la decisione speculare all'estensione e
   * ha lo stesso peso — riammette su tutta la rete qualcuno che almeno un ente
   * aveva bloccato (AJ10).
   */
  const [revoking, setRevoking] = useState<GlobalBan>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * Il perché manca, e va detto — non lasciato indovinare da un pulsante
   * spento (WCAG 3.3.2, AS5). Uno stato solo per le due conferme, come il
   * campo: ne è aperta una per volta.
   */
  const [reasonMissing, setReasonMissing] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const askingHeadingRef = useRef<HTMLHeadingElement>(null);
  const { say, announcer } = useAnnounce();

  const read = useCallback((signal: AbortSignal) => loadBlacklist({ signal }), []);
  const {
    data,
    failed,
    reload: load,
  } = useLoad(read, {
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });
  const bans = data?.bans;
  const candidates = data?.candidates;

  useEffect(() => {
    if (asking || revoking) askingHeadingRef.current?.focus();
  }, [asking, revoking]);

  /**
   * Chiudendo la conferma, il fuoco torna da dove era partito.
   *
   * AJ16 elencava questa schermata fra quelle corrette — "vale per la scheda di
   * una conversazione, per quella di un ente e per l'annullamento della
   * conferma nella blacklist" — e per le prime due era vero. Qui no: la
   * conferma prendeva il fuoco all'andata e lo lasciava cadere sul `<body>` al
   * ritorno, e nessuna prova guardava il ritorno. Trovato correggendo lo stesso
   * difetto nella moderazione, ed è la ragione per cui i due sono corretti
   * insieme: una voce che dice "fatto" e non lo è costa più di una che dice
   * "da fare".
   *
   * Annullando si torna al pulsante da cui si era partiti: non è cambiato
   * niente. Confermando si torna al titolo della sezione, perché l'elenco
   * viene riletto e quel pulsante può non esistere più — un blocco appena
   * esteso non è più un candidato.
   *
   * **E l'esito si annuncia dopo aver spostato il fuoco, non prima.** Uno
   * spostamento di fuoco interrompe la coda di parlato di NVDA, JAWS e
   * VoiceOver: dicendolo prima, l'annuncio veniva zittito dal titolo su cui il
   * fuoco stava arrivando. È la stessa correzione della moderazione, per la
   * stessa ragione.
   */
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const pending = useRef<{ focus: HTMLButtonElement | null; said?: string } | null>(null);
  // Senza elenco di dipendenze, di proposito: il lavoro va fatto al primo
  // disegno in cui la sezione si è fermata, che non coincide con il cambio di
  // nessuna singola variabile.
  useEffect(() => {
    const job = pending.current;
    if (!job || asking || revoking) return;
    pending.current = null;

    const node = job.focus;
    if (node?.isConnected && !node.disabled) node.focus();
    else headingRef.current?.focus();

    if (job.said) say(job.said);
  });

  /** Chi ha aperto la conferma, per saperci tornare annullando. */
  const open = (event: React.MouseEvent<HTMLButtonElement>, what: () => void): void => {
    pending.current = null;
    openerRef.current = event.currentTarget;
    /*
      Il modulo riparte **vuoto**, e sono due cose distinte.

      Il motivo scritto per un blocco e non confermato restava nel campo
      aprendo la conferma di un altro: bastava premere, e quel perché — scritto
      pensando a qualcun altro — finiva nel registro, che è append-only. E il
      segno di "manca il motivo" restava alzato, quindi la scheda nuova nasceva
      con il campo già dichiarato non valido e un errore che parlava di
      un'azione abbandonata (AS7).
    */
    setReason('');
    setReasonMissing(false);
    /*
      Una conferma per volta. Aprendo "estendi" con "revoca" già aperta, i due
      blocchi si disegnavano insieme: due caselle che condividono lo stesso
      `reason`, due pulsanti con lo stesso nome accessibile, e
      `askingHeadingRef` assegnato a due titoli — quindi il fuoco all'andata
      finiva su quello sbagliato.
    */
    setAsking(undefined);
    setRevoking(undefined);
    what();
  };

  const cancelAsking = (): void => {
    pending.current = { focus: openerRef.current };
    setAsking(undefined);
    setRevoking(undefined);
    setReason('');
    setReasonMissing(false);
  };

  /**
   * `Esc` chiude la conferma, come "Annulla".
   *
   * Passa dalla stessa funzione, così il ritorno del fuoco resta quello di
   * AJ16 e non nasce una seconda strada che un giorno diverge.
   */
  const onEscape = (event: React.KeyboardEvent): void => {
    if (event.key !== 'Escape' || busy) return;
    event.stopPropagation();
    cancelAsking();
  };

  /**
   * Il perché, o si dice cosa manca.
   *
   * Prima il fuoco e poi l'annuncio (AP4): `focus()` è sincrono, `say`
   * programma un disegno che arriva dopo, quindi lo spostamento non zittisce
   * la frase.
   */
  const requireReason = (): string | undefined => {
    const why = reason.trim();
    if (why) return why;
    setReasonMissing(true);
    reasonRef.current?.focus();
    say(t('platform.reason_required'), 'danger');
    return undefined;
  };

  const confirm = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!asking || busy) return;
    const why = requireReason();
    if (!why) return;
    setBusy(true);
    try {
      await addToBlacklist({ banId: asking.banId, reason: why });
      // Il candidato appena esteso esce dall'elenco: il bersaglio è il titolo.
      pending.current = { focus: null, said: t('platform.blacklist_added') };
      setAsking(undefined);
      setReason('');
      await load();
    } catch (error) {
      say(`${t('platform.blacklist_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const current = revoking;
    if (!current || busy) return;
    const why = requireReason();
    if (!why) return;
    setBusy(true);
    try {
      await removeFromBlacklist(current.id, { reason: why });
      // Il blocco appena revocato esce dall'elenco: il bersaglio è il titolo.
      pending.current = { focus: null, said: t('platform.blacklist_removed') };
      setRevoking(undefined);
      setReason('');
      await load();
    } catch (error) {
      say(`${t('platform.blacklist_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="blacklist-heading">
      <div className="section-head">
        <div className="section-head__title">
          <h3 id="blacklist-heading" ref={headingRef} tabIndex={-1}>
            {t('platform.blacklist_title')}
          </h3>
          <InfoTip>{t('platform.blacklist_hint')}</InfoTip>
        </div>
      </div>

      {announcer}

      {asking ? (
        /*
          `<section>` e non `<div>`: e' un riquadro con un titolo e un compito
          dentro, e da quando ascolta `Esc` deve essere un elemento a cui si
          possa attribuire un ruolo. Il titolo gli da' il nome, come nella
          scheda della moderazione.
        */
        <section className="note" aria-labelledby="blacklist-reason-heading" onKeyDown={onEscape}>
          <h4 id="blacklist-reason-heading" ref={askingHeadingRef} tabIndex={-1}>
            {t('platform.blacklist_confirm_title', { hashPreview: asking.hashPreview })}
          </h4>
          {/*
            `noValidate`: senza, il `required` qui sotto fa bloccare la submit
            dal browser con una nuvoletta nativa non tradotta e non annunciata,
            e il gestore non viene nemmeno chiamato — il pulsante sembra non
            fare niente. Il `required` resta perché è quello che dice all'albero
            di accessibilità che il campo è obbligatorio.
          */}
          <form onSubmit={(event) => void confirm(event)} noValidate>
            <div className="field">
              <label htmlFor="blacklist-reason">{t('platform.reason_label')}</label>
              <textarea
                id="blacklist-reason"
                ref={reasonRef}
                rows={2}
                maxLength={500}
                required
                /* Come nella scheda dell'ente: l'errore e' legato al campo (AS8). */
                aria-describedby={
                  reasonMissing
                    ? 'blacklist-reason-error blacklist-reason-hint'
                    : 'blacklist-reason-hint'
                }
                aria-invalid={reasonMissing ? true : undefined}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonMissing(false);
                }}
              />
              {/*
                Dice che è obbligatorio — il pulsante non lo dice più, e non lo
                diceva comunque a chi non vede — e dove finisce quel testo.
              */}
              {reasonMissing ? (
                <p className="alert" id="blacklist-reason-error">
                  {t('platform.reason_required')}
                </p>
              ) : null}
              <p className="muted" id="blacklist-reason-hint">
                {t('platform.blacklist_reason_hint')}
              </p>
            </div>
            {/*
              Cambia etichetta, **non** si disabilita: il fuoco è su questo
              pulsante nell'istante in cui si preme, e disabilitarlo lo toglie
              dal percorso di tabulazione scaricandolo sul `<body>` proprio
              mentre si aspetta un esito (X3, U2). Il doppio tocco lo ferma la
              guardia dentro il gestore.

              E **non si disabilita nemmeno a motivo vuoto** (AS5): era l'unico
              segnale del fatto che il motivo serve, e un pulsante disabilitato
              si attraversa in silenzio. Adesso si preme, e premendolo si sente
              cosa manca.
            */}
            <button type="submit" className="button button--primary">
              {busy ? t('platform.saving') : t('platform.blacklist_confirm')}
            </button>
            <button type="button" className="button button--quiet" onClick={cancelAsking}>
              {t('report_user.cancel')}
            </button>
          </form>
        </section>
      ) : null}

      {revoking ? (
        <section className="note" aria-labelledby="blacklist-revoke-heading" onKeyDown={onEscape}>
          <h4 id="blacklist-revoke-heading" ref={askingHeadingRef} tabIndex={-1}>
            {t('platform.blacklist_revoke', { hashPreview: revoking.hashPreview })}
          </h4>
          {/* Come sopra: `required` per l'albero di accessibilità, `noValidate`
              perché a dirlo a parole ci pensa il prodotto. */}
          <form onSubmit={(event) => void revoke(event)} noValidate>
            <div className="field">
              <label htmlFor="blacklist-revoke-reason">
                {t('platform.blacklist_revoke_reason')}
              </label>
              <textarea
                id="blacklist-revoke-reason"
                ref={reasonRef}
                rows={2}
                maxLength={500}
                required
                /* Come nella scheda dell'ente: l'errore e' legato al campo (AS8). */
                aria-describedby={
                  reasonMissing
                    ? 'blacklist-revoke-reason-error blacklist-revoke-reason-hint'
                    : 'blacklist-revoke-reason-hint'
                }
                aria-invalid={reasonMissing ? true : undefined}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonMissing(false);
                }}
              />
              {/*
                Dice che è obbligatorio — il pulsante non lo dice più, e non lo
                diceva comunque a chi non vede — e dove finisce quel testo.
              */}
              {reasonMissing ? (
                <p className="alert" id="blacklist-revoke-reason-error">
                  {t('platform.reason_required')}
                </p>
              ) : null}
              <p className="muted" id="blacklist-revoke-reason-hint">
                {t('platform.blacklist_reason_hint')}
              </p>
            </div>
            {/*
              Come sopra: etichetta, non disabilitazione — e non disabilitato
              nemmeno a motivo vuoto.

              E dice **revoca**, non "estendi". Riusava la chiave dell'altra
              conferma, quindi il nome accessibile diceva l'azione opposta a
              quella che eseguiva. Finché il pulsante nasceva disabilitato la
              cosa era mitigata; togliendo `disabled` — cioè proprio per chi
              naviga fra i controlli — è diventato il primo elemento che quella
              persona incontra dentro una scheda di revoca (AS7).
            */}
            <button type="submit" className="button button--primary">
              {busy ? t('platform.saving') : t('platform.blacklist_revoke_confirm')}
            </button>
            <button type="button" className="button button--quiet" onClick={cancelAsking}>
              {t('report_user.cancel')}
            </button>
          </form>
        </section>
      ) : null}

      <div className="section-head">
        <h4>{t('platform.blacklist_current')}</h4>
        {bans ? (
          <span className="count">{t('platform.count_bans', { count: bans.length })}</span>
        ) : null}
      </div>
      {!bans && !failed ? <Loading label={t('admin.loading')} /> : null}
      {bans && bans.length === 0 ? <Empty text={t('platform.blacklist_empty')} /> : null}
      <ul className="plain-list">
        {bans?.map((ban) => (
          <li key={ban.id} className="row">
            <div className="stack stack--tight">
              <p className="row__title">
                <code>{ban.hashPreview}</code>
              </p>
              <p className="row__meta">{ban.reason}</p>
              <p className="row__meta">
                {[
                  t('moderation.ban_since', {
                    when: formatWhen(ban.createdAt, i18n.resolvedLanguage, undefined),
                  }),
                  ban.expiresAt
                    ? t('moderation.ban_until', {
                        when: formatWhen(ban.expiresAt, i18n.resolvedLanguage, undefined),
                      })
                    : t('platform.blacklist_two_years'),
                ].join(' · ')}
              </p>
            </div>
            <div className="row__actions">
              <button
                type="button"
                className="button"
                disabled={busy}
                aria-label={t('platform.blacklist_revoke', { hashPreview: ban.hashPreview })}
                onClick={(event) => {
                  open(event, () => setRevoking(ban));
                  setReason('');
                }}
              >
                {t('platform.blacklist_revoke_short')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="section-head">
        <h4>{t('platform.blacklist_candidates')}</h4>
        {candidates ? (
          <span className="count">
            {t('platform.count_candidates', { count: candidates.length })}
          </span>
        ) : null}
      </div>
      {candidates && candidates.length === 0 ? (
        <Empty text={t('platform.blacklist_candidates_empty')} />
      ) : null}
      <ul className="plain-list">
        {candidates?.map((candidate) => (
          <li key={candidate.banId} className="row">
            <div className="stack stack--tight">
              <p className="row__title">
                <code>{candidate.hashPreview}</code>
              </p>
              <p className="row__meta">
                {t('platform.blacklist_candidate_line', {
                  count: candidate.tenants,
                  when: formatWhen(candidate.lastAt, i18n.resolvedLanguage, undefined),
                })}
              </p>
            </div>
            <div className="row__actions">
              <button
                type="button"
                className="button"
                disabled={busy}
                aria-label={t('platform.blacklist_add', { hashPreview: candidate.hashPreview })}
                onClick={(event) => open(event, () => setAsking(candidate))}
              >
                {t('platform.blacklist_add_short')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/*
        Duecento caratteri che spiegano **perché** questa sezione esiste: si
        leggono una volta, e da lì in poi sono la riga che si salta prima di
        arrivare ai blocchi. Dietro una riga che si apre restano trovabili — il
        testo è nel documento, la ricerca del browser lo trova — e stanno
        **dopo** l'elenco, come nelle tre viste della rendicontazione: il
        commento diceva "senza stare ogni volta fra il titolo e l'elenco" e il
        codice lo metteva esattamente lì.
      */}
      <details className="more">
        <summary>{t('platform.blacklist_candidates_why')}</summary>
        <p>{t('platform.blacklist_candidates_hint')}</p>
      </details>

      <p className="note">{t('platform.blacklist_legal_note')}</p>
    </section>
  );
}

// ------------------------------------------------------ infrastruttura media

/**
 * L'infrastruttura media (mock 4, `s-clusters`).
 *
 * Era il sottoinsieme "tecnico" di `HealthSection`: la lista dei cluster, il
 * loro costo, e dove spostare un ente stavano sotto lo stesso titolo dei KPI
 * in diretta e dei job di retention — tre compiti senza rapporto fra loro
 * (`docs/DECISIONI.md`, BB4). Carica i propri dati: è lo stesso costo di una
 * seconda richiesta di `platformHealth`/`platformTenants` che paga anche
 * `HealthSection`, contro la stessa complessità di farle dipendere l'una
 * dall'altra per uno stato che nessuna delle due possiede da sola.
 */
function ClustersSection({
  headingRef,
}: {
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const { say, announcer } = useAnnounce();

  const read = useCallback((signal: AbortSignal) => platformHealth({ signal }), []);
  const {
    data: health,
    failed,
    reload,
  } = useLoad(read, {
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  /**
   * Gli enti, per la select di "sposta un ente" (mock 4, `s-clusters`).
   *
   * Carica separatamente: non è la lista principale della schermata, ed è
   * una sola tornata di enti attivi e non, senza consumo del mese — questa
   * sezione non lo mostra.
   */
  const readTenants = useCallback((signal: AbortSignal) => platformTenants({ signal }), []);
  const { data: tenantList, reload: reloadTenants } = useLoad(readTenants, {
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const when = (iso: string | null): string =>
    iso ? formatWhen(iso, i18n.resolvedLanguage, undefined) : t('platform.never');

  // ---------------------------------------------------- costo per cluster

  const [costEditingId, setCostEditingId] = useState<string>();
  const [costValue, setCostValue] = useState('');
  const [costReason, setCostReason] = useState('');
  const [costBusy, setCostBusy] = useState(false);
  const [costReasonMissing, setCostReasonMissing] = useState(false);
  const costReasonRef = useRef<HTMLTextAreaElement>(null);
  const costHeadingRef = useRef<HTMLHeadingElement>(null);
  /**
   * Chi ha aperto il pannello, per restituirgli il fuoco chiudendolo — lo
   * stesso pattern di `openerRef`/`pending` usato sopra per moderazione e
   * blacklist (AJ16), e per la stessa ragione: annunciare **dopo** aver
   * spostato il fuoco, non prima, altrimenti NVDA/JAWS/VoiceOver zittiscono
   * l'annuncio dato mentre il fuoco si sposta.
   */
  const costOpenerRef = useRef<HTMLButtonElement | null>(null);
  const costPending = useRef<{ focus: HTMLButtonElement | null; said?: string } | null>(null);

  // Senza elenco di dipendenze, di proposito: come per `pending` sopra.
  useEffect(() => {
    if (costEditingId) {
      costHeadingRef.current?.focus();
      return;
    }
    const job = costPending.current;
    if (!job || costBusy) return;
    costPending.current = null;
    const node = job.focus;
    if (node?.isConnected && !node.disabled) node.focus();
    else headingRef.current?.focus();
    if (job.said) say(job.said);
  });

  const startCostEdit = (
    clusterId: string,
    current: string | null,
    opener: HTMLButtonElement,
  ): void => {
    costPending.current = null;
    costOpenerRef.current = opener;
    setCostEditingId(clusterId);
    setCostValue(current ?? '');
    setCostReason('');
    setCostReasonMissing(false);
  };

  const cancelCostEdit = (): void => {
    costPending.current = { focus: costOpenerRef.current };
    setCostEditingId(undefined);
  };

  const saveCost = async (clusterId: string): Promise<void> => {
    if (costBusy) return;
    const why = costReason.trim();
    if (!why) {
      setCostReasonMissing(true);
      costReasonRef.current?.focus();
      say(t('platform.cluster_cost_reason_required'), 'danger');
      return;
    }
    setCostBusy(true);
    try {
      const trimmed = costValue.trim();
      await setClusterCost(clusterId, { monthlyCost: trimmed ? trimmed : null, reason: why });
      costPending.current = {
        focus: costOpenerRef.current,
        said: t('platform.cluster_cost_saved'),
      };
      setCostEditingId(undefined);
      await reload();
    } catch (error) {
      say(`${t('platform.cluster_cost_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setCostBusy(false);
    }
  };

  // ------------------------------------------------------- sposta un ente

  const [moveTenantId, setMoveTenantId] = useState('');
  const [moveClusterId, setMoveClusterId] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveReasonMissing, setMoveReasonMissing] = useState(false);
  const moveReasonRef = useRef<HTMLTextAreaElement>(null);

  const currentClusterName = (): string => {
    const tenant = tenantList?.tenants.find((row) => row.id === moveTenantId);
    if (!tenant?.livekitClusterId) return t('platform.cluster_move_current_none');
    return (
      health?.clusters.find((cluster) => cluster.id === tenant.livekitClusterId)?.name ??
      t('platform.cluster_move_current_none')
    );
  };

  const submitMove = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (moveBusy || !moveTenantId || !moveClusterId) return;
    const why = moveReason.trim();
    if (!why) {
      setMoveReasonMissing(true);
      moveReasonRef.current?.focus();
      say(t('platform.reason_required'), 'danger');
      return;
    }
    setMoveBusy(true);
    try {
      const result = await setTenantCluster(moveTenantId, {
        clusterId: moveClusterId,
        reason: why,
      });
      const tenantName =
        tenantList?.tenants.find((row) => row.id === moveTenantId)?.name ?? moveTenantId;
      say(t('platform.cluster_move_success', { tenant: tenantName, cluster: result.cluster.name }));
      setMoveReason('');
      setMoveReasonMissing(false);
      setMoveClusterId('');
      /**
       * Il campo "Cluster attuale" legge da `tenantList`, non da `health`:
       * senza ricaricare anche quello, dopo uno spostamento riuscito la
       * schermata continuerebbe a mostrare il cluster vecchio proprio nel
       * momento in cui si vuole verificare l'esito (rilievo di code-reviewer).
       */
      await Promise.all([reload(), reloadTenants()]);
    } catch (error) {
      say(`${t('platform.cluster_move_failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setMoveBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="clusters-heading">
      <div className="section-head">
        <div className="section-head__title">
          <h3 id="clusters-heading" ref={headingRef} tabIndex={-1}>
            {t('platform.clusters_section_title')}
          </h3>
          <InfoTip>{t('platform.clusters_section_hint')}</InfoTip>
        </div>
      </div>
      {announcer}
      {!health && !failed ? <Loading label={t('admin.loading')} /> : null}

      {health ? (
        <>
          <div className="section-head">
            <h4>{t('platform.clusters_title')}</h4>
            <span className="count">
              {t('platform.count_clusters', { count: health.clusters.length })}
            </span>
          </div>
          <ul className="plain-list">
            {health.clusters.map((cluster) => (
              <li key={cluster.id} className={cluster.isActive ? 'row' : 'row row--flagged'}>
                <div className="stack stack--tight">
                  <p className="row__title">{cluster.name}</p>
                  <p className="row__meta">
                    {t('platform.cluster_line', {
                      kind: cluster.kind,
                      region: cluster.region,
                      tenants: cluster.tenants,
                    })}
                  </p>
                  <p className="row__meta">
                    {t('platform.cluster_last_event', { when: when(cluster.lastWebhookAt) })}
                  </p>
                  {/*
                    "Nessuna stringa vuota muta": un costo non impostato dice
                    esplicitamente di non esserlo, invece di lasciare la riga
                    corta e ambigua fra "zero" e "non lo sappiamo".
                  */}
                  <p className="row__meta">
                    {cluster.monthlyCost
                      ? t('platform.cluster_cost', { cost: cluster.monthlyCost })
                      : t('platform.cluster_cost_none')}
                  </p>
                </div>
                <div className="row__actions">
                  {cluster.isActive ? null : (
                    <span className="badge badge--closed">{t('platform.cluster_inactive')}</span>
                  )}
                  <button
                    type="button"
                    className="button"
                    disabled={costBusy}
                    aria-label={t('platform.cluster_cost_edit', { name: cluster.name })}
                    onClick={(event) =>
                      startCostEdit(cluster.id, cluster.monthlyCost, event.currentTarget)
                    }
                  >
                    {t('platform.cluster_cost_edit_short')}
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {costEditingId
            ? (() => {
                const cluster = health.clusters.find((row) => row.id === costEditingId);
                if (!cluster) return null;
                return (
                  <section className="note" aria-labelledby="cluster-cost-heading">
                    <h4 id="cluster-cost-heading" ref={costHeadingRef} tabIndex={-1}>
                      {t('platform.cluster_cost_edit', { name: cluster.name })}
                    </h4>
                    <div className="field">
                      <label htmlFor="cluster-cost-value">{t('platform.cluster_cost_label')}</label>
                      <input
                        id="cluster-cost-value"
                        value={costValue}
                        maxLength={120}
                        aria-describedby="cluster-cost-hint"
                        onChange={(event) => setCostValue(event.target.value)}
                      />
                      <p className="muted" id="cluster-cost-hint">
                        {t('platform.cluster_cost_hint')}
                      </p>
                    </div>
                    <div className="field">
                      <label htmlFor="cluster-cost-reason">{t('platform.reason_label')}</label>
                      <textarea
                        id="cluster-cost-reason"
                        ref={costReasonRef}
                        value={costReason}
                        maxLength={500}
                        required
                        aria-describedby={
                          costReasonMissing
                            ? 'cluster-cost-reason-error cluster-cost-reason-hint'
                            : 'cluster-cost-reason-hint'
                        }
                        aria-invalid={costReasonMissing ? true : undefined}
                        onChange={(event) => {
                          setCostReason(event.target.value);
                          if (costReasonMissing) setCostReasonMissing(false);
                        }}
                      />
                      {costReasonMissing ? (
                        <p className="alert" id="cluster-cost-reason-error">
                          {t('platform.cluster_cost_reason_required')}
                        </p>
                      ) : null}
                      <p className="muted" id="cluster-cost-reason-hint">
                        {t('platform.cluster_cost_reason_hint')}
                      </p>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="button button--primary"
                        disabled={costBusy}
                        onClick={() => void saveCost(cluster.id)}
                      >
                        {t('platform.cluster_cost_save')}
                      </button>
                      <button
                        type="button"
                        className="button"
                        disabled={costBusy}
                        onClick={cancelCostEdit}
                      >
                        {t('platform.cluster_cost_cancel')}
                      </button>
                    </div>
                  </section>
                );
              })()
            : null}

          {/*
            "Sposta un ente" (mock 4, `s-clusters`): solo il cluster di
            destinazione — quello di partenza si legge, non si scrive mai da
            un input. Il testo spiega perché nessuna sessione in corso ne
            risente, non solo che è così (vincolo 5).
          */}
          <div className="section-head">
            <h4>{t('platform.cluster_move_title')}</h4>
          </div>
          {/*
            Non un `InfoTip`, a differenza delle altre schede di questa
            pagina: questa frase non è un contesto ripetuto, è la conferma
            del vincolo 5 proprio davanti al pulsante che sposta un ente
            vero — chi non l'ha mai fatto è anche chi più ha bisogno di
            leggerla prima di premere, non dopo aver pensato di aprire
            un'icona (`ui-design`, in revisione).
          */}
          <p className="card__sub">{t('platform.cluster_move_hint')}</p>
          <form onSubmit={(event) => void submitMove(event)}>
            <div className="field">
              <label htmlFor="cluster-move-tenant">{t('platform.cluster_move_tenant_label')}</label>
              <select
                id="cluster-move-tenant"
                value={moveTenantId}
                required
                onChange={(event) => setMoveTenantId(event.target.value)}
              >
                <option value="" disabled>
                  {t('platform.cluster_move_tenant_label')}
                </option>
                {tenantList?.tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="cluster-move-current">
                {t('platform.cluster_move_current_label')}
              </label>
              <input id="cluster-move-current" value={currentClusterName()} disabled readOnly />
            </div>
            <div className="field">
              <label htmlFor="cluster-move-new">{t('platform.cluster_move_new_label')}</label>
              <select
                id="cluster-move-new"
                value={moveClusterId}
                required
                onChange={(event) => setMoveClusterId(event.target.value)}
              >
                <option value="" disabled>
                  {t('platform.cluster_move_new_label')}
                </option>
                {health.clusters
                  .filter((cluster) => cluster.isActive)
                  .map((cluster) => (
                    <option key={cluster.id} value={cluster.id}>
                      {cluster.name}
                    </option>
                  ))}
              </select>
            </div>
            <p className="note">{t('platform.cluster_move_live_note')}</p>
            <div className="field">
              <label htmlFor="cluster-move-reason">{t('platform.reason_label')}</label>
              <textarea
                id="cluster-move-reason"
                ref={moveReasonRef}
                value={moveReason}
                maxLength={500}
                required
                aria-describedby={
                  moveReasonMissing
                    ? 'cluster-move-reason-error cluster-move-reason-hint'
                    : 'cluster-move-reason-hint'
                }
                aria-invalid={moveReasonMissing ? true : undefined}
                onChange={(event) => {
                  setMoveReason(event.target.value);
                  if (moveReasonMissing) setMoveReasonMissing(false);
                }}
              />
              {moveReasonMissing ? (
                <p className="alert" id="cluster-move-reason-error">
                  {t('platform.reason_required')}
                </p>
              ) : null}
              <p className="muted" id="cluster-move-reason-hint">
                {t('platform.reason_hint')}
              </p>
            </div>
            <div className="actions">
              <button
                type="submit"
                className="button button--primary"
                disabled={moveBusy || !moveTenantId || !moveClusterId}
              >
                {t('platform.cluster_move_submit')}
              </button>
            </div>
          </form>
        </>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------ monitoraggio

/**
 * Monitoraggio (mock 4, `s-health`).
 *
 * La parte che la fase 7 aggiunge, e che vale più delle altre: **lo stato del
 * job di retention, regola per regola**. Una riga senza data di ultima
 * esecuzione è una cancellazione dichiarata e mai fatta, ed è esattamente la
 * violazione che si accumula in silenzio (sezione 7quinquies).
 *
 * È una delle due sezioni che non hanno nessun controllo: si guardano e basta,
 * quindi devono leggersi come un cruscotto. I quattro numeri in cima ci sono
 * sempre stati; quello che mancava è che le due liste sotto dicessero **con una
 * parola** quali righe non vanno — la pastiglia accanto al nome della regola,
 * non solo la frase in fondo alla riga.
 *
 * L'infrastruttura media, che stava qui dentro, è ora una sezione propria
 * (`ClustersSection`, sopra): resta il cruscotto, esce ciò che si tocca
 * (`docs/DECISIONI.md`, BB4).
 */
function HealthSection({ headingRef }: { headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  const { t, i18n } = useTranslation();
  /**
   * Anche qui l'errore si **annuncia**.
   *
   * Erano le due sole sezioni della fase 7 che scrivevano un messaggio a
   * schermo senza montare la regione live: con l'API caduta, un fornitore cieco
   * restava su "Caricamento…" e l'errore esisteva solo per chi vede
   * (WCAG 4.1.3, AJ15). Adesso la regione e il banner arrivano insieme, e
   * montarne solo metà non è più possibile.
   */
  const { say, announcer } = useAnnounce();

  const read = useCallback((signal: AbortSignal) => platformHealth({ signal }), []);
  const { data: health, failed } = useLoad(read, {
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const when = (iso: string | null): string =>
    iso ? formatWhen(iso, i18n.resolvedLanguage, undefined) : t('platform.never');

  return (
    <section className="card" aria-labelledby="health-heading">
      <div className="section-head">
        <div className="section-head__title">
          <h3 id="health-heading" ref={headingRef} tabIndex={-1}>
            {t('platform.health_title')}
          </h3>
          <InfoTip>{t('platform.health_hint')}</InfoTip>
        </div>
      </div>
      {announcer}
      {!health && !failed ? <Loading label={t('admin.loading')} /> : null}

      {health ? (
        <>
          <dl className="facts">
            <div>
              <dt>{t('platform.kpi_live')}</dt>
              <dd>{health.live.sessions}</dd>
            </div>
            <div>
              <dt>{t('platform.kpi_staff')}</dt>
              <dd>{health.live.staffOnline}</dd>
            </div>
            <div>
              <dt>{t('platform.kpi_alerts')}</dt>
              <dd>{health.live.openAlerts}</dd>
            </div>
            <div>
              <dt>{t('platform.kpi_webhooks')}</dt>
              <dd>{health.live.webhooksLastHour}</dd>
            </div>
          </dl>

          <div className="section-head">
            <h4>{t('platform.retention_title')}</h4>
            <span className="count">
              {t('platform.count_rules', { count: health.retention.length })}
            </span>
          </div>
          <ul className="plain-list">
            {health.retention.map((rule) => {
              /**
               * Una riga **aperta e mai chiusa** non è un'esecuzione riuscita.
               *
               * Il job apre la riga prima di cancellare e la chiude dopo: un
               * processo ucciso in mezzo — un riavvio, un `OOM`, un container
               * fermato durante un rilascio — lascia `finished_at` nullo per
               * sempre. Fino a ieri questa schermata leggeva solo la data di
               * **inizio** e scriveva "ultima esecuzione … · 0 righe", cioè
               * raccontava come conclusa con zero cancellazioni una regola che
               * non era mai arrivata in fondo. È il caso peggiore travestito da
               * caso normale, in una schermata che esiste perché una violazione
               * non si accumuli in silenzio (`docs/DECISIONI.md`, AO3).
               *
               * Non serve nessuna soglia e nessun orologio: la riga o è chiusa
               * o non lo è. Un giro davvero in corso in questo istante si legge
               * allo stesso modo — "iniziata, mai conclusa" — che è vero anche
               * per lui, e dura i pochi secondi del job.
               */
              const unfinished = Boolean(rule.lastRunAt) && !rule.finishedAt;
              const wrong = Boolean(rule.error) || !rule.lastRunAt || unfinished;
              return (
                <li key={rule.task} className={wrong ? 'row row--flagged' : 'row'}>
                  <div className="stack stack--tight">
                    <p className="row__title">{rule.task}</p>
                    <p className="row__meta">{t('platform.retention_days', { days: rule.days })}</p>
                    <p className="row__meta">
                      {!rule.lastRunAt
                        ? t('platform.retention_never')
                        : unfinished
                          ? t('platform.retention_unfinished', { when: when(rule.lastRunAt) })
                          : t('platform.retention_last', {
                              when: when(rule.lastRunAt),
                              rows: rule.rows ?? 0,
                            })}
                    </p>
                    {rule.error ? (
                      <p className="row__meta">
                        {t('platform.retention_error', { error: rule.error })}
                      </p>
                    ) : null}
                  </div>
                  {/*
                    Una parola sola per lo stato della regola: in un elenco si
                    guarda la colonna, non si legge la frase di ogni riga. La
                    frase resta accanto, perché è lei a portare la data.
                  */}
                  <span className={wrong ? 'badge badge--closed' : 'badge badge--open'}>
                    {t(
                      rule.error
                        ? 'platform.rule_failed'
                        : !rule.lastRunAt
                          ? 'platform.rule_never'
                          : unfinished
                            ? 'platform.rule_unfinished'
                            : 'platform.rule_done',
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          {/*
            Duecentottantaquattro caratteri che spiegano come si legge la
            colonna qui sopra: si leggono una volta nella vita, e poi sarebbero
            la riga da saltare ogni volta per arrivare ai dati. Le pastiglie
            dicono la stessa cosa in una parola; questo resta per chi vuole
            sapere perché quella parola è quella — **dopo** l'elenco, come
            nelle tre viste della rendicontazione. Il commento lo diceva già e
            il codice faceva il contrario.
          */}
          <details className="more">
            <summary>{t('platform.retention_how')}</summary>
            <p>{t('platform.retention_hint')}</p>
          </details>
          <p className="note">{t('platform.no_personal_data_note')}</p>
        </>
      ) : null}
    </section>
  );
}

// ------------------------------------------------------ accessi impersonati

function AccessSection({ headingRef }: { headingRef: React.RefObject<HTMLHeadingElement | null> }) {
  const { t, i18n } = useTranslation();
  const { say, announcer } = useAnnounce();

  const read = useCallback((signal: AbortSignal) => impersonationLog({ signal }), []);
  const { data, failed } = useLoad(read, {
    onError: (error) => say(`${t('platform.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });
  const entries = data?.entries;

  return (
    <section className="card" aria-labelledby="access-heading">
      <div className="section-head">
        <div className="section-head__title">
          <h3 id="access-heading" ref={headingRef} tabIndex={-1}>
            {t('platform.access_title')}
          </h3>
          <InfoTip>{t('platform.access_hint')}</InfoTip>
        </div>
        {entries ? (
          <span className="count">{t('platform.count_entries', { count: entries.length })}</span>
        ) : null}
      </div>
      {announcer}
      {!entries && !failed ? <Loading label={t('admin.loading')} /> : null}
      {entries && entries.length === 0 ? <Empty text={t('platform.access_empty')} /> : null}

      <ul className="plain-list">
        {entries?.map((entry) => (
          <li key={`${entry.id}-${entry.occurredAt}`} className="row">
            <div className="stack stack--tight">
              <p className="row__title">{entry.tenant ?? t('platform.alert_platform_wide')}</p>
              <p className="row__meta">
                {[
                  entry.admin ?? '—',
                  t(`admin.action_${entry.action.replaceAll('.', '_')}`, {
                    defaultValue: entry.action,
                  }),
                  formatWhen(entry.occurredAt, i18n.resolvedLanguage, undefined),
                ].join(' · ')}
              </p>
              {entry.reason ? (
                <p className="row__meta">{t('admin.entry_reason', { reason: entry.reason })}</p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      <p className="note">{t('platform.access_footer')}</p>
    </section>
  );
}

// ------------------------------------------------------------------ supporto

/**
 * Il dettaglio tecnico di un avviso.
 *
 * Sono conteggi e nomi di compiti — mai un dato di una persona: la riga in
 * tabella lo garantisce, e questa funzione non fa che leggerla. Resta in forma
 * di `chiave: valore` perché è materiale per chi mantiene il servizio, non una
 * frase per un cliente.
 */
function describeAlert(
  t: TFunction,
  kind: string,
  detail: Record<string, string | number | boolean>,
): string {
  /**
   * Quante persone non hanno ricevuto risposta merita una frase.
   *
   * `sessions: 12 · windowHours: 24` è materiale per chi mantiene il servizio,
   * e per gli avvisi di sistema va bene. Ma qui quel numero è **l'unico posto**
   * in cui compare quante persone hanno chiamato senza trovare nessuno, e
   * consegnarlo in forma di coppia chiave-valore è consegnarlo a metà.
   */
  if (kind === 'unanswered_calls' && typeof detail.sessions === 'number') {
    return t('platform.alert_unanswered_detail', {
      count: detail.sessions,
      hours: detail.windowHours,
    });
  }
  return describeDetail(detail);
}

function describeDetail(detail: Record<string, string | number | boolean>): string {
  return Object.entries(detail)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ');
}
